// src/pages/ProductDetailsPage.js
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";
import CartDrawer from "../components/CartDrawer";
import { flyImageToCart } from "../utils/flyToCart";
import {
  getAllProducts,
  getProductBySlug,
  rateProduct,
} from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { PRODUCT_CATEGORIES, PRODUCT_TAGS } from "../data/products";
import { useSeo } from "../hooks/useSeo";
import { FaAlignLeft, FaCubes, FaRulerVertical, FaTruck } from "react-icons/fa";
import { apiRequest } from "../services/api";
import {
  getActiveFreeShippingDiscount,
  getActiveOrderDiscount,
} from "../services/discounts.api";
import { buildFreeShippingInfo } from "../utils/freeShippingDiscount";
import { buildOrderDiscountInfo } from "../utils/orderDiscountInfo";
import {
  getProductInstagramReelsSection,
  getProductLookSection,
} from "../services/homepage.api";
import {
  saveProductReturnPosition,
  restoreProductReturnPosition,
} from "../utils/navigationMemory";

function ProductDetailsPage() {
  const { lang } = useContext(LanguageContext);
  const [principalCategory, setPrincipalCategory] = useState(null);
  
  const [instagramReelsSection, setInstagramReelsSection] = useState(null);

  const [productLookSection, setProductLookSection] = useState(null);
const [autoLookProducts, setAutoLookProducts] = useState([]);
const productLookScrollRef = useRef(null);
const desktopMediaRefs = useRef({});

  const [searchParams, setSearchParams] = useSearchParams();

  const [shipping, setShipping] = useState({
    fee: 4,
    free_enabled: false,
    free_threshold: 0,
  });

  const [freeShippingDiscount, setFreeShippingDiscount] = useState(null);

  const {
  addToCart,
  addBxgyDiscountItem,
  toggleWishlist,
  isInWishlist,
  cartItems,
  subtotal,
} = useCart();
  const navigate = useNavigate();
  const { slug } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  const [selectedRating, setSelectedRating] = useState(0);
const [hoverRating, setHoverRating] = useState(0);
const [ratingSaving, setRatingSaving] = useState(false);
const [ratingMessage, setRatingMessage] = useState("");

  const [mediaDirection, setMediaDirection] = useState("next");

  const [quantity, setQuantity] = useState(1);
const [viewerZoomed, setViewerZoomed] = useState(false);

  // ✅ separate color selection from size selection
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const [openDesc, setOpenDesc] = useState(false);
  const [openMaterial, setOpenMaterial] = useState(false);
  const [openAge, setOpenAge] = useState(false);
  const [openShip, setOpenShip] = useState(false);

  const [isPlacing, setIsPlacing] = useState(false);
  const [error, setError] = useState("");

  const [addedModal, setAddedModal] = useState(null);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [bxgyPreviewOpen, setBxgyPreviewOpen] = useState(false);

  const productKey = product?.id || product?.seo_slug || null;

  const mainImageRef = useRef(null);
  const viewerScrollRef = useRef(null);

  const normalizeColor = (v) =>
    String(v || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[-_]/g, "");

  const setColorInUrl = (colorName) => {
    const params = new URLSearchParams(searchParams);
    params.set("color", normalizeColor(colorName));
    setSearchParams(params, { replace: true });
  };

  const colorFromUrl = searchParams.get("color");

  const sourceCategoryFromUrl =
  searchParams.get("sourceCategory") ||
  searchParams.get("selectedCategory") ||
  searchParams.get("category") ||
  "";

const sourceTagFromUrl = searchParams.get("tag") || "";

  const normalizedColorFromUrl = colorFromUrl
    ? normalizeColor(colorFromUrl)
    : null;


  useEffect(() => {
    apiRequest("/shipping-settings")
      .then((s) =>
        setShipping({
          fee: Number(s.default_fee ?? 4),
          free_enabled: !!s.free_enabled,
          free_threshold: Number(s.free_threshold ?? 0),
        })
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
  getActiveFreeShippingDiscount()
    .then(setFreeShippingDiscount)
    .catch(() => setFreeShippingDiscount(null));
}, []);

  useEffect(() => {
  getProductInstagramReelsSection()
    .then(setInstagramReelsSection)
    .catch(() => {});
}, []);

useEffect(() => {
  getProductLookSection()
    .then(setProductLookSection)
    .catch(() => {});
}, []);

  /* ---------------- FETCH PRODUCT ---------------- */
useEffect(() => {
  let mounted = true;

  setLoading(true);
  setProduct(null);
  setActiveIndex(0);
  setMediaDirection("next");
  setSelectedColor(null);
  setSelectedVariant(null);
  setError("");
  setQuantity(1);
  setViewerZoomed(false);

  setSelectedRating(0);
setHoverRating(0);
setRatingSaving(false);
setRatingMessage("");

  getProductBySlug(slug)
      .then((data) => {
        if (!mounted) return;

        setProduct(data);
setActiveIndex(0);
setMediaDirection("next");

        // ✅ auto-select color/size when there is only one valid option
const availableProductVariants = (data.variants || []).filter(
  (v) => Number(v.stock_qty) > 0
);

const colorNames = Array.from(
  new Set(availableProductVariants.map((v) => v.color_name).filter(Boolean))
);

if (availableProductVariants.length > 0 && !normalizedColorFromUrl) {
  // If there is only one color, select it automatically.
  if (colorNames.length === 1) {
    const onlyColor = colorNames[0];

    setSelectedColor(onlyColor);

    const variantsForOnlyColor = availableProductVariants.filter(
      (v) => v.color_name === onlyColor
    );

    // If this selected color has only one available size, select it automatically.
    if (variantsForOnlyColor.length === 1) {
      setSelectedVariant(variantsForOnlyColor[0]);
    } else {
      setSelectedVariant(null);
    }
  } else {
    // Keep old behavior: select first color so sizes show immediately.
    setSelectedColor(availableProductVariants[0].color_name);
    setSelectedVariant(null);
  }
}
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    return () => {
      mounted = false;
    };
  }, [slug]);

  const formatBreadcrumbLabel = (value) => {
  return String(value || "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const backToSourceCategory = () => {
  if (!sourceCategoryFromUrl) return;

  const params = new URLSearchParams();

  if (sourceCategoryFromUrl) {
    params.set("category", sourceCategoryFromUrl);
  }

  if (sourceTagFromUrl) {
    params.set("tag", sourceTagFromUrl);
  }

  navigate(`/products?${params.toString()}`);
};

  const getTagsForDetailsProduct = (p) => {
    const tags = [];
    if (p?.is_new_in) tags.push(PRODUCT_TAGS.NEW_IN);
    if (p?.is_bestseller) tags.push(PRODUCT_TAGS.BESTSELLER);
    if (Number(p?.sale_percent) > 0) tags.push(PRODUCT_TAGS.SALE);
    return tags;
  };

  const getTagsForCardProduct = (p) => {
    const tags = Array.isArray(p?.tags) ? [...p.tags] : [];
    if (Number(p?.discount) > 0 && !tags.includes(PRODUCT_TAGS.SALE)) {
      tags.push(PRODUCT_TAGS.SALE);
    }
    return tags;
  };

  // ✅ only variants that can actually be bought
  const availableVariants = useMemo(() => {
    return (product?.variants || []).filter((v) => Number(v.stock_qty) > 0);
  }, [productKey]);

  const productTags = useMemo(() => {
    if (!product) return [];
    const tags = [];
    if (product.is_new_in) tags.push(PRODUCT_TAGS.NEW_IN);
    if (product.is_bestseller) tags.push(PRODUCT_TAGS.BESTSELLER);
    if (Number(product.sale_percent) > 0) tags.push(PRODUCT_TAGS.SALE);
    return tags;
  }, [productKey]);

  /* ---------------- DERIVED DATA (SAFE HOOKS) ---------------- */

  const colors = useMemo(() => {
    if (!availableVariants.length) return [];

    const map = new Map();

    availableVariants.forEach((v) => {
      if (!map.has(v.color_name)) {
        map.set(v.color_name, v.color_hex);
      }
    });

    return Array.from(map.entries()).map(([name, code]) => ({
      name,
      code,
    }));
  }, [availableVariants.length]);

  // ✅ sizes depend on selectedColor, not selectedVariant
  const sizesByColor = useMemo(() => {
    if (!selectedColor) return [];

    return availableVariants.filter((v) => v.color_name === selectedColor);
  }, [availableVariants.length, selectedColor]);

  useEffect(() => {
  if (!selectedColor) return;
  if (selectedVariant) return;

  if (sizesByColor.length === 1) {
    setSelectedVariant(sizesByColor[0]);
    setQuantity(1);
    setError("");
  }
}, [selectedColor, selectedVariant, sizesByColor]);

 const normalizeLookCategory = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getLookCategory = (p) => {
  if (!p) return "";

  if (p.primary_category) return normalizeLookCategory(p.primary_category);
  if (p.category) return normalizeLookCategory(p.category);

  if (Array.isArray(p.categories) && p.categories.length > 0) {
    return normalizeLookCategory(p.categories[0]);
  }

  return "";
};

const getLookTagSlugs = (p) => {
  const rawTags = Array.isArray(p?.tags) ? p.tags : [];

  const tagSlugs = rawTags
    .map((tag) => {
      if (typeof tag === "string") return tag;
      return tag?.slug || tag?.label_en || tag?.label_ar || "";
    })
    .map((tag) => String(tag || "").trim().toLowerCase())
    .filter(Boolean);

  const systemTags = getTagsForDetailsProduct(p)
    .map((tag) => String(tag || "").trim().toLowerCase())
    .filter(Boolean);

  return Array.from(new Set([...tagSlugs, ...systemTags]));
};

const countMutualLookTags = (productA, productB) => {
  const tagsA = new Set(getLookTagSlugs(productA));
  const tagsB = getLookTagSlugs(productB);

  return tagsB.filter((tag) => tagsA.has(tag)).length;
};
 
useEffect(() => {
  let mounted = true;

  if (!product?.id && !product?.seo_slug) {
    setPrincipalCategory(null);
    setAutoLookProducts([]);
    return;
  }

  getAllProducts({})
    .then((rows) => {
      if (!mounted) return;

      const mapped = (rows || []).map(mapBackendProduct);

      const currentFromCatalog = mapped.find((p) => {
        const sameId = Number(p.id) === Number(product.id);
        const sameSlug =
          String(p.seo_slug || p.slug || "") ===
          String(product.seo_slug || product.slug || "");

        return sameId || sameSlug;
      });

      const currentCategory =
        getLookCategory(currentFromCatalog) || getLookCategory(product);

      setPrincipalCategory(currentCategory || null);

      if (!currentCategory) {
        setAutoLookProducts([]);
        return;
      }

      const mainCategory = normalizeLookCategory(currentCategory);

      const otherProducts = mapped.filter((p) => {
        const sameId = Number(p.id) === Number(product.id);
        const sameSlug =
          String(p.seo_slug || p.slug || "") ===
          String(product.seo_slug || product.slug || "");

        return !sameId && !sameSlug;
      });

      const anotherCategoryProducts = otherProducts.filter((p) => {
        const productCategory = getLookCategory(p);
        return productCategory && productCategory !== mainCategory;
      });

      const productsWithMutualTags = anotherCategoryProducts
        .map((p) => ({
          ...p,
          __mutualTagCount: countMutualLookTags(product, p),
        }))
        .filter((p) => p.__mutualTagCount > 0)
        .sort((a, b) => {
          if (b.__mutualTagCount !== a.__mutualTagCount) {
            return b.__mutualTagCount - a.__mutualTagCount;
          }

          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });

      const fallbackProducts = anotherCategoryProducts
        .filter(
          (p) =>
            !productsWithMutualTags.some(
              (matched) => Number(matched.id) === Number(p.id)
            )
        )
        .sort(
          (a, b) =>
            new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );

      const finalProducts =
        productsWithMutualTags.length >= 5
          ? productsWithMutualTags
          : [
              ...productsWithMutualTags,
              ...fallbackProducts.slice(0, 5 - productsWithMutualTags.length),
            ];

      setAutoLookProducts(finalProducts);
    })
    .catch(() => {
      if (!mounted) return;
      setPrincipalCategory(null);
      setAutoLookProducts([]);
    });

  return () => {
    mounted = false;
  };
}, [product?.id, product?.seo_slug]);

  const totalStock = useMemo(() => {
    const arr = product?.variants || [];
    return arr.reduce((sum, v) => sum + (Number(v.stock_qty) || 0), 0);
  }, [productKey]);

  const stockInfo = useMemo(() => {
    const t = Number(totalStock) || 0;

    if (t <= 0) {
      return {
        text: lang === "EN" ? "OUT OF STOCK" : "غير متوفر",
        cls: "text-[#7A7674]",
      };
    }
    if (t <= 3) {
      return {
        text: lang === "EN" ? `ONLY ${t} LEFT` : `باقي ${t} فقط`,
        cls: "text-[#C25B2A]",
      };
    }
    return {
      text: lang === "EN" ? "IN STOCK" : "متوفر",
      cls: "text-green-600",
    };
  }, [totalStock, lang]);

  // ✅ UI / SEO derived values (MUST be before useSeo)
  const displayName = product
    ? lang === "EN"
      ? product.name_en
      : product.name_ar
    : "";

  const breadcrumbSchema = product
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: lang === "EN" ? "Home" : "الرئيسية",
            item: window.location.origin,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: lang === "EN" ? "Products" : "المنتجات",
            item: `${window.location.origin}/all-products`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: displayName,
            item: window.location.href,
          },
        ],
      }
    : null;

  const description = product
    ? lang === "EN"
      ? product.description_en || product.short_desc_en || ""
      : product.description_ar || product.short_desc_ar || ""
    : "";

  const discount = Number(product?.sale_percent) || 0;
  const hasDiscount = discount > 0;

  const finalPrice = product
    ? hasDiscount
      ? Math.round(product.base_price * (1 - discount / 100))
      : product.base_price
    : 0;

    // ✅ BXGY LOGIC (ADD HERE)
const bxgyDetails = Array.isArray(product?.bxgy_details)
  ? product.bxgy_details
  : [];

const buyItem = bxgyDetails.find((i) => i.role === "buy");
const getItem = bxgyDetails.find((i) => i.role === "get");

const isBxgyBuyProduct =
  !!buyItem && Number(buyItem.product_id) === Number(product.id);

const isSameProduct =
  !!buyItem &&
  !!getItem &&
  Number(buyItem.product_id) === Number(getItem.product_id);

const bxgyLabel = (() => {
  if (!isBxgyBuyProduct || !buyItem || !getItem) return "";

  const buy = Number(buyItem.buy_qty || getItem.buy_qty || 0);
  const get = Number(getItem.get_qty || buyItem.get_qty || 0);

  const bxgyDiscountType =
    getItem.discount_type || getItem.get_discount_type || "free";

  const bxgyDiscountValue =
    getItem.discount_value ?? getItem.get_discount_value ?? 0;

  const getPublicId =
    getItem.public_id || getItem.publicId || getItem.name_en || "";

  if (bxgyDiscountType === "free") {
    return lang === "AR"
      ? `اشترِ ${buy} واحصل على ${get} ${getPublicId} مجاناً`
      : `Buy ${buy} and get ${get} ${getPublicId} FREE`;
  }

  return lang === "AR"
    ? `اشترِ ${buy} واحصل على ${get} ${getPublicId} بخصم ${bxgyDiscountValue}%`
    : `Buy ${buy} and get ${get} ${getPublicId} ${bxgyDiscountValue}% OFF`;
})();

  /* ---------------- TEXT ---------------- */

  const TEXT = {
    EN: {
      addToBag: "ADD TO CART",
      selectVariant: "Please select size and color.",
      description: "Description",
      delivery: "Delivery & Returns",
      deliveryText:
        "Delivery across Lebanon. Free over $50. Cash on delivery available.",
      returnsText:
        "You can exchange sizes within 3 days if the product is unworn and in original packaging.",
      color: "Color",
      size: "Size",
      material: "Material",
      age: "Age",
      shipping: "Shipping & Delivery",
      pickupInfo: "Pick up from store",
      deliveryInfo: "Delivery fees",
      freeDeliveryInfo: "Free delivery on orders above",
      cm: "cm",
    },
    AR: {
      addToBag: "أضيفي إلى السلة",
      selectVariant: "يرجى اختيار اللون والمقاس.",
      description: "الوصف",
      delivery: "التوصيل والاستبدال",
      deliveryText:
        "توصيل داخل لبنان. توصيل مجاني للطلبات فوق 50$. الدفع عند الاستلام متوفر.",
      returnsText:
        "يمكنك تغيير المقاس خلال 3 أيام في حال لم يتم ارتداء المنتج وكان في علبته الأصلية.",
      color: "اللون",
      size: "المقاس",
      material: "الخامة",
      age: "العمر",
      shipping: "التوصيل والاستلام",
      pickupInfo: "استلام من المحل",
      deliveryInfo: "رسوم التوصيل",
      freeDeliveryInfo: "توصيل مجاني للطلبات فوق",
      cm: "سم",
    },
  }[lang];

useSeo(
  "product",
  product?.id,
  product
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Product",
            "@id": window.location.href,
            name: displayName,
            description: description?.slice(0, 500),
            sku: product.public_id,
            mpn: product.public_id,
            image: product.images?.map((i) => i.url),
            brand: {
              "@type": "Brand",
              name: "Etika",
            },
            category: principalCategory || "Women’s Clothing",
            offers: {
              "@type": "Offer",
              url: window.location.href,
              priceCurrency: "USD",
              price: Number(finalPrice).toFixed(2),
              availability:
                totalStock > 0
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
              itemCondition: "https://schema.org/NewCondition",
            },
          },

          breadcrumbSchema,
        ],
      }
    : null
);

  useEffect(() => {
    if (!product?.id) return;

    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "view_product",
        entity_type: "product",
        entity_id: product.id,
      }),
    }).catch(() => {});
  }, [product?.id]);

  const colorToImageIndex = useMemo(() => {
    if (!product?.images) return {};

    const map = {};

    product.images.forEach((img, index) => {
      if (!img.color_name) return;

      const key = normalizeColor(img.color_name);

      // if first time OR lower sort_order → replace
      if (
        map[key] === undefined ||
        Number(img.sort_order) <
          Number(product.images[map[key]]?.sort_order ?? Infinity)
      ) {
        map[key] = index;
      }
    });

    return map;
  }, [productKey]);

  const scrollToColorImage = (colorName) => {
    const index = colorToImageIndex[normalizeColor(colorName)];
    if (index !== undefined && index !== activeIndex) {
      setActiveIndex(index);
    }
  };

  const media = useMemo(() => {
    return product?.images || [];
  }, [productKey]);

useEffect(() => {
  if (!media.length) return;

  const indexesToPreload = Array.from(
    new Set([
      activeIndex,
      (activeIndex + 1) % media.length,
      activeIndex === 0 ? media.length - 1 : activeIndex - 1,
    ])
  );

  indexesToPreload.forEach((index) => {
    const item = media[index];
    const url =
      typeof item === "string"
        ? item
        : item?.url || item?.image || item?.image_url || "";

    if (!url) return;

    const isVideoUrl = /\.(mp4|webm|ogg|mov)$/i.test(
      String(url).split("?")[0]
    );

    if (isVideoUrl) return;

    const img = new Image();
    img.src = url;
  });
}, [media, activeIndex]);

  const activeMedia = media[activeIndex];
  const isVideo = /\.(mp4|webm|ogg)$/i.test(activeMedia?.url);

  const getMediaUrl = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.url || item.image || item.image_url || "";
};

const isVideoMedia = (item) => {
  const url = getMediaUrl(item);
  return /\.(mp4|webm|ogg|mov)$/i.test(String(url).split("?")[0]);
};

const activeMediaUrl = getMediaUrl(activeMedia);

const desktopVisibleMedia = media.length
  ? [
      media[activeIndex],
      media[(activeIndex + 1) % media.length],
    ].filter(Boolean)
  : [];

  const desktopVisibleThumbs = media.length
  ? Array.from({ length: Math.min(4, media.length) }, (_, offset) => {
      const index = (activeIndex + offset) % media.length;
      return {
        item: media[index],
        index,
      };
    })
  : [];

const visibleMobileThumbs =
  media.length > 1
    ? [
        media[activeIndex],
        media[(activeIndex + 1) % media.length],
      ].filter(Boolean)
    : media.length === 1
    ? [media[activeIndex]]
    : [];

const selectedVariantStock = Number(selectedVariant?.stock_qty || 0);

const isOutOfStock = totalStock <= 0;
const canAddToBag =
  Boolean(selectedColor) &&
  Boolean(selectedVariant) &&
  selectedVariantStock > 0 &&
  !isPlacing;

const quantityMax = selectedVariant
  ? Math.max(0, Number(selectedVariant.stock_qty || 0))
  : Math.max(0, Number(totalStock || 0));

const stockBadgeText = (() => {
  const stock = selectedVariant ? selectedVariantStock : totalStock;

  if (Number(stock) <= 0) {
    return lang === "EN" ? "OUT OF STOCK" : "غير متوفر";
  }

  if (Number(stock) <= 3) {
    if (Number(stock) === 1) {
      return lang === "EN" ? "LAST ITEM" : "آخر قطعة";
    }

    return lang === "EN"
      ? `LAST ${stock} ITEMS`
      : `آخر ${stock} قطع`;
  }

  return "";
})();

useEffect(() => {
  if (!quantityMax) {
    setQuantity(1);
    return;
  }

  setQuantity((q) => Math.min(Math.max(1, q), quantityMax));
}, [quantityMax]);

const colorImageOptions = colors.map((color) => {
  const imageIndex = colorToImageIndex[normalizeColor(color.name)];
  const mediaItem =
    imageIndex !== undefined ? media[imageIndex] : media[0];

  return {
    ...color,
    imageIndex,
    mediaItem,
    imageUrl: getMediaUrl(mediaItem),
    isVideo: isVideoMedia(mediaItem),
  };
});

  const goToMedia = (nextIndex, direction = "next") => {
  if (!media.length) return;

  setMediaDirection(direction);
  setViewerZoomed(false);
  setActiveIndex(nextIndex);
};

const prevMedia = () => {
  if (!media.length) return;

  setMediaDirection("prev");
  setViewerZoomed(false);
  setActiveIndex((i) => (i === 0 ? media.length - 1 : i - 1));
};

const nextMedia = () => {
  if (!media.length) return;

  setMediaDirection("next");
  setViewerZoomed(false);
  setActiveIndex((i) => (i === media.length - 1 ? 0 : i + 1));
};

  useEffect(() => {
  if (!product || !normalizedColorFromUrl || selectedColor) return;

  // find matching color name from product variants
  const match = (product.variants || []).find(
    (v) => normalizeColor(v.color_name) === normalizedColorFromUrl
  );

  if (!match) return;

  // ✅ set selected color
  setSelectedColor(match.color_name);
  setSelectedVariant(null);
  setError("");

  // ✅ switch image to first image of that color
  scrollToColorImage(match.color_name);
}, [productKey, normalizedColorFromUrl]);

useEffect(() => {
  if (loading || !product) return;

  const timer = setTimeout(() => {
    restoreProductReturnPosition();
  }, 120);

  return () => clearTimeout(timer);
}, [loading, product?.id, autoLookProducts.length]);

/* ---------------- EARLY RETURNS ---------------- */

if (loading) {
  return (
    <div
      className="w-full min-h-screen flex flex-col overflow-x-hidden bg-[#F6EFE9]"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      {/* MOBILE SKELETON */}
      <div className="md:hidden bg-[#F6EFE9] px-4 pt-0 pb-10">
        <div className="relative mb-3 mt-0 overflow-hidden">
          <div className="flex items-center gap-2 py-3">
            <div className="h-4 w-[80px] bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-[10px] bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-[120px] bg-gray-200 animate-pulse rounded" />
          </div>
          <div className="h-px bg-[#d9d9d9] mb-5" />
        </div>

        <div className="grid grid-cols-[22%_1fr] gap-3 items-start">
          <div className="flex flex-col gap-[6px]">
            <div className="w-full aspect-[3/4] bg-gray-200 animate-pulse" />
            <div className="w-full aspect-[3/4] bg-gray-200 animate-pulse" />
          </div>

          <div className="relative bg-[#f7f7f7] top-5 aspect-[4/5] overflow-hidden">
            <div className="w-full h-full bg-gray-200 animate-pulse" />
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center">
          <div className="h-7 w-[80%] bg-gray-200 animate-pulse rounded" />
          <div className="mt-5 h-9 w-[120px] bg-gray-200 animate-pulse rounded" />
        </div>

        <div className="mt-10">
          <div className="h-5 w-[130px] bg-gray-200 animate-pulse rounded mb-2" />
          <div className="h-px bg-[#cfcfcf] mb-3" />
          <div className="flex gap-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="w-[68px] aspect-[3/4] bg-gray-200 animate-pulse"
              />
            ))}
          </div>
        </div>

        <div className="mt-12">
          <div className="h-5 w-[70px] bg-gray-200 animate-pulse rounded mb-2" />
          <div className="h-px bg-[#cfcfcf] mb-3" />
          <div className="flex gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-[43px] h-[43px] bg-gray-200 animate-pulse"
              />
            ))}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <div className="w-[72%] h-[64px] bg-gray-200 animate-pulse" />
          <div className="w-[62px] h-[62px] bg-gray-200 animate-pulse" />
        </div>
      </div>

      {/* DESKTOP SKELETON */}
      <div className="hidden md:block w-full bg-[#F6EFE9] mt-[165px]">
        <div className="w-full px-[1px] py-3">
          <div className="flex items-center gap-2">
            <div className="h-4 w-[90px] bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-[10px] bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-[160px] bg-gray-200 animate-pulse rounded" />
          </div>
        </div>

        <div className="w-[60%] mx-auto h-px bg-[#d8d8d8]" />

        <div
          className="
            max-w-none
            w-full
            mx-0
            px-[1px]
            pt-8
            grid
            grid-cols-[118px_minmax(0,1fr)_390px]
            gap-2
            items-start
          "
        >
          <aside className="sticky top-[125px] self-start -mt-6 flex flex-col gap-[3px]">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-[112px] h-[158px] bg-gray-200 animate-pulse"
              />
            ))}
          </aside>

          <main className="min-w-0">
            <div className="grid grid-cols-2 gap-0 bg-white">
              <div className="aspect-[3/4] bg-gray-200 animate-pulse" />
              <div className="aspect-[3/4] bg-gray-200 animate-pulse" />
            </div>
          </main>

          <aside className="sticky top-[135px] self-start bg-[#F6EFE9] pb-10">
            <div className="pl-2">
              <div className="mx-auto h-7 w-[85%] bg-gray-200 animate-pulse rounded" />
              <div className="mx-auto mt-5 h-7 w-[120px] bg-gray-200 animate-pulse rounded" />

              <div className="mt-10">
                <div className="h-5 w-[130px] bg-gray-200 animate-pulse rounded mb-3" />
                <div className="h-px bg-[#cfcfcf] mb-3" />
                <div className="flex gap-2">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="w-[54px] h-[74px] bg-gray-200 animate-pulse"
                    />
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <div className="h-5 w-[70px] bg-gray-200 animate-pulse rounded mb-3" />
                <div className="h-px bg-[#cfcfcf] mb-3" />
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-[43px] h-[43px] bg-gray-200 animate-pulse"
                    />
                  ))}
                </div>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <div className="w-[72%] h-[64px] bg-gray-200 animate-pulse" />
                <div className="w-[62px] h-[62px] bg-gray-200 animate-pulse" />
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer className="mt-auto" />
    </div>
  );
}

 if (!product) {
  return (
    <div
      className="w-full min-h-screen flex flex-col bg-[#F6EFE9]"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      <section className="border-t border-black bg-[#F6EFE9] mt-0 md:mt-[156px]">
        <div className="max-w-[900px] mx-auto px-5 md:px-6 py-16 md:py-20 text-center">
          <h1 className="text-[30px] md:text-[36px] leading-none font-bold text-[#333]">
            {lang === "AR" ? "المنتج غير موجود" : "Product not found"}
          </h1>

          <p className="mt-4 text-[14px] md:text-[15px] text-[#666] leading-relaxed">
            {lang === "AR"
              ? "لم نتمكن من العثور على هذا المنتج. يمكنك العودة إلى المتجر ومتابعة التسوق."
              : "We could not find this product. You can return to the store and continue shopping."}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/all-products")}
              className="w-full sm:w-[230px] h-[50px] bg-[#e99a22] text-white text-[15px] font-normal hover:opacity-90 active:scale-[0.99] transition"
            >
              {lang === "AR" ? "متابعة التسوق" : "Continue shopping"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full sm:w-[230px] h-[50px] bg-white border border-black text-black text-[15px] font-normal hover:bg-[#f7f7f7] transition"
            >
              {lang === "AR" ? "العودة" : "Go back"}
            </button>
          </div>
        </div>
      </section>

      <Footer className="mt-auto" />
    </div>
  );
}

  /* ---------------- UI DATA ---------------- */

  const material = lang === "EN" ? product.material_en : product.material_ar;

  const age = product.age_range;

  const inWishlist = isInWishlist(product.id);

  const dimensionsText =
  lang === "EN"
    ? product.dimensions_composition_en || product.short_desc_en || ""
    : product.dimensions_composition_ar || product.short_desc_ar || "";

const productDescriptionText =
  lang === "EN"
    ? product.description_en || ""
    : product.description_ar || "";

const relatedTags = Array.isArray(product.tags)
  ? product.tags.filter((tag) => tag?.slug)
  : [];

const getRelatedTagLabel = (tag) => {
  if (!tag) return "";

  return lang === "AR"
    ? tag.label_ar || tag.label_en || tag.slug
    : tag.label_en || tag.slug;
};

const getLookProductSlug = (p) => {
  return p?.slug || p?.seo_slug || "";
};

const goToLookProduct = (p, e) => {
  const nextSlug = getLookProductSlug(p);
  if (!nextSlug) return;

  saveProductReturnPosition({
    productId: p.id,
    element: e?.currentTarget,
  });

  navigate(`/product/${encodeURIComponent(nextSlug)}`);
};

const goToTag = (tagSlug) => {
  if (!tagSlug) return;
  navigate(`/products?tag=${encodeURIComponent(tagSlug)}`);
};

const productInstagramReels = Array.isArray(instagramReelsSection?.reels)
  ? instagramReelsSection.reels.filter((item) => item?.video_url)
  : [];

const productInstagramTitle =
  lang === "AR"
    ? instagramReelsSection?.title_ar || "شاهدونا على السوشيال ميديا❤️"
    : instagramReelsSection?.title_en || "See us on social media❤️";

  const productLookProducts = autoLookProducts;

const productLookTitle =
  lang === "AR"
    ? productLookSection?.title_ar || "المنتجات المستخدمة في الإطلالة"
    : productLookSection?.title_en || "PRODUCTS USED IN LOOK";

const getLookProductImage = (p) => {
  if (!p) return "/images/placeholder-product.jpg";

  if (Array.isArray(p.images) && p.images[0]) {
    return p.images[0];
  }

  return p.image || p.image_url || "/images/placeholder-product.jpg";
};

const resolveLookMedia = (url) => {
  if (!url) return "/images/placeholder-product.jpg";
  if (String(url).startsWith("http")) return url;

  const base =
    process.env.REACT_APP_API_URL?.replace(/\/api$/, "") ||
    window.location.origin;

  return base + url;
};

const getLookProductMedia = (p) => {
  if (!p) return "/images/placeholder-product.jpg";

  if (Array.isArray(p.images) && p.images[0]) {
    const first = p.images[0];

    if (typeof first === "string") {
      return resolveLookMedia(first);
    }

    return resolveLookMedia(first.url || first.image || first.image_url);
  }

  return resolveLookMedia(
    p.image || p.image_url || "/images/placeholder-product.jpg"
  );
};

const isLookProductVideo = (p) => {
  const url = getLookProductMedia(p);
  return /\.(mp4|webm|ogg|mov)$/i.test(String(url).split("?")[0]);
};

const getLookProductPrice = (p) => {
  const base = Number(p?.price ?? p?.base_price ?? 0);
  const sale = Number(p?.discount ?? p?.sale_percent ?? 0);

  if (sale > 0) {
    return Math.round(base * (1 - sale / 100));
  }

  return base;
};

const getLookProductName = (p) => {
  if (!p) return "";

  return lang === "AR"
    ? p.nameAR || p.name_ar || p.nameEN || p.name_en || ""
    : p.nameEN || p.name_en || p.nameAR || p.name_ar || "";
};

const scrollProductLook = (direction) => {
  const el = productLookScrollRef.current;
  if (!el) return;

  const amount = el.clientWidth * 0.85;
  el.scrollBy({
    left: direction === "next" ? amount : -amount,
    behavior: "smooth",
  });
};

    const resolveInstagramVideo = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;

  const base =
    process.env.REACT_APP_API_URL?.replace(/\/api$/, "") ||
    window.location.origin;

  return base + url;
};

const InstagramIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[20px] h-[20px]" fill="none">
    <defs>
      <radialGradient id="igAppGradientA" cx="30%" cy="107%" r="115%">
        <stop offset="0%" stopColor="#FEDA75" />
        <stop offset="25%" stopColor="#FA7E1E" />
        <stop offset="50%" stopColor="#D62976" />
        <stop offset="75%" stopColor="#962FBF" />
        <stop offset="100%" stopColor="#4F5BD5" />
      </radialGradient>
    </defs>

    <rect
      x="4"
      y="4"
      width="40"
      height="40"
      rx="11"
      fill="url(#igAppGradientA)"
    />

    <rect
      x="14"
      y="14"
      width="20"
      height="20"
      rx="6"
      stroke="white"
      strokeWidth="3"
    />

    <circle
      cx="24"
      cy="24"
      r="5"
      stroke="white"
      strokeWidth="3"
    />

    <circle cx="30.5" cy="17.5" r="2" fill="white" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" className="w-[19px] h-[19px]" fill="currentColor">
    <path d="M12 21s-7.2-4.4-9.6-8.6C.3 8.8 2.4 4.5 6.5 4.5c2.2 0 3.7 1.2 4.5 2.4.8-1.2 2.3-2.4 4.5-2.4 4.1 0 6.2 4.3 4.1 7.9C19.2 16.6 12 21 12 21z" />
  </svg>
);

const DimensionIcon = () => (
  <svg viewBox="0 0 32 32" className="w-[26px] h-[26px]" fill="none">
    <path
      d="M7 22L22 7L25 10L10 25L7 22Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M11 18L13 20" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M14 15L17 18" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M17 12L19 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <path d="M20 9L23 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const TranslationIcon = () => (
  <svg viewBox="0 0 24 24" className="w-[24px] h-[24px]" fill="none">
    <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 9H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M8 12H12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M7 18L5 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="17" cy="10" r="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M15 14C15.7 13.3 18.3 13.3 19 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const WashIcon = () => (
  <svg viewBox="0 0 32 32" className="w-[32px] h-[32px]" fill="none">
    <path d="M5 11L8 25H24L27 11" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 11C10 8 12 14 14 11C16 8 18 14 20 11C22 8 24 14 27 11" stroke="currentColor" strokeWidth="1.4" />
    <path d="M13 7L15 4" stroke="currentColor" strokeWidth="1.2" />
    <path d="M17 7L21 3" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const IronIcon = () => (
  <svg viewBox="0 0 32 32" className="w-[32px] h-[32px]" fill="none">
    <path d="M5 20H27L23 12H9L5 20Z" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 20V24H25" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="20" cy="15" r="1" fill="currentColor" />
  </svg>
);

const CrossBoxIcon = () => (
  <svg viewBox="0 0 32 32" className="w-[32px] h-[32px]" fill="none">
    <rect x="6" y="6" width="20" height="20" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6 6L26 26" stroke="currentColor" strokeWidth="1.4" />
    <path d="M26 6L6 26" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const CrossCircleIcon = () => (
  <svg viewBox="0 0 32 32" className="w-[32px] h-[32px]" fill="none">
    <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 8L24 24" stroke="currentColor" strokeWidth="1.4" />
    <path d="M24 8L8 24" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const BleachIcon = () => (
  <svg viewBox="0 0 32 32" className="w-[32px] h-[32px]" fill="none">
    <path d="M16 6L28 25H4L16 6Z" stroke="currentColor" strokeWidth="1.4" />
    <path d="M6 25L26 6" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const DeliveryIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[56px] h-[56px]" fill="none">
    <path d="M4 16H27V34H4V16Z" fill="#cfcfcf" />
    <path d="M27 22H37L43 29V34H27V22Z" fill="#cfcfcf" />
    <circle cx="14" cy="36" r="4" fill="#cfcfcf" />
    <circle cx="35" cy="36" r="4" fill="#cfcfcf" />
    <path d="M10 23L15 28L24 17" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PickupIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[56px] h-[56px]" fill="none">
    <path d="M9 18H39L36 40H12L9 18Z" fill="#cfcfcf" />
    <path
      d="M15 18C15 12.5 18.8 8 24 8C29.2 8 33 12.5 33 18"
      stroke="#cfcfcf"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M17 28L22 33L32 23"
      stroke="white"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const deliveryFeeLabel = (() => {
  const fee = Number(shipping.fee || 0);

  if (fee <= 0) {
    return lang === "AR" ? "مجاني" : "Free";
  }

  return `$${fee.toFixed(2)}`;
})();

const FreeDeliveryIcon = () => (
  <svg viewBox="0 0 48 48" className="w-[56px] h-[56px]" fill="none">
    <path d="M6 16H28V34H6V16Z" fill="#cfcfcf" />
    <path d="M28 22H38L43 28V34H28V22Z" fill="#cfcfcf" />
    <circle cx="15" cy="36" r="4" fill="#cfcfcf" />
    <circle cx="36" cy="36" r="4" fill="#cfcfcf" />
    <path
      d="M13 24L18 29L28 18"
      stroke="white"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M34 10V18"
      stroke="#cfcfcf"
      strokeWidth="4"
      strokeLinecap="round"
    />
    <path
      d="M30 14H38"
      stroke="#cfcfcf"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

const totalItemsForFreeShipping = cartItems.reduce(
  (sum, item) => sum + Number(item.qty || 0),
  0
);

const freeShippingInfo = buildFreeShippingInfo({
  discount: freeShippingDiscount,
  subtotal,
  totalItems: totalItemsForFreeShipping,
  lang,
});

const hasFreeShippingNoCondition =
  freeShippingInfo.hasDiscount &&
  freeShippingInfo.minRequirement === "none";

const showDeliveryFeeIcon = !hasFreeShippingNoCondition;
const showFreeDeliveryIcon = freeShippingInfo.hasDiscount;

const DeliveryBenefits = ({ compact = false }) => {
  const itemWidth = compact ? "w-[110px]" : "w-[145px]";
  const titleSize = compact ? "text-[10px]" : "text-[14px]";
  const valueSize = compact ? "text-[10px]" : "text-[14px]";
  const gap = compact ? "gap-6" : "gap-8";

  return (
    <section className={`${compact ? "mt-7 flex justify-center text-center pt-5" : "mt-8"}`}>
      <div className={`flex items-start justify-center ${gap}`}>
        {showDeliveryFeeIcon && (
          <div className={`${itemWidth} flex flex-col items-center text-center`}>
            <DeliveryIcon />

            <p className={`mt-2 ${titleSize} font-extrabold uppercase leading-[1.25]`}>
              {TEXT.deliveryInfo}
            </p>

            <p className={`mt-2 ${valueSize} font-extrabold`}>
              {deliveryFeeLabel}
            </p>
          </div>
        )}

        {showFreeDeliveryIcon && (
          <div className={`${itemWidth} flex flex-col items-center text-center`}>
            <FreeDeliveryIcon />

            <p className={`mt-2 ${titleSize} font-extrabold uppercase leading-[1.25]`}>
              {lang === "AR" ? "توصيل مجاني" : "Free delivery"}
            </p>

            <p className={`mt-2 ${valueSize} font-extrabold`}>
              {freeShippingInfo.conditionLabel}
            </p>

            {freeShippingInfo.endLabel && (
              <p className={`mt-1 ${compact ? "text-[9px]" : "text-[12px]"} text-gray-600 leading-[1.25]`}>
                {lang === "AR"
                  ? `ينتهي في ${freeShippingInfo.endLabel}`
                  : `Ends on ${freeShippingInfo.endLabel}`}
              </p>
            )}
          </div>
        )}

        <div className={`${itemWidth} flex flex-col items-center text-center`}>
          <PickupIcon />

          <p className={`mt-2 ${titleSize} font-extrabold uppercase leading-[1.25]`}>
            {TEXT.pickupInfo}
          </p>

          <p className={`mt-2 ${valueSize} font-extrabold`}>
            {lang === "AR" ? "مجاني" : "Free"}
          </p>
        </div>
      </div>
    </section>
  );
};

  /* ---------------- ACTIONS ---------------- */

  const formatModalPrice = (value) => {
  const n = Number(value || 0);
  return `$${n.toFixed(2)}`;
};

const getBxgyUpsellText = (suggestion) => {
  if (!suggestion) return "";

  const discountText =
    suggestion.discountType === "free"
      ? lang === "AR"
        ? "مجاناً"
        : "FREE"
      : lang === "AR"
      ? `بخصم ${suggestion.discountValue}%`
      : `${suggestion.discountValue}% OFF`;

  const getPublicId =
    suggestion.getPublicId ||
    suggestion.public_id ||
    suggestion.publicId ||
    suggestion.nameEN ||
    "";

  if (suggestion.action === "complete_percentage_same_product") {
    return lang === "AR"
      ? `أضيفي ${suggestion.missingQty} أكثر واحصلي على الأخيرة ${discountText}`
      : `Add ${suggestion.missingQty} more to get the last one ${discountText}`;
  }

  if (suggestion.action === "add_buy_more") {
    return lang === "AR"
      ? `أضيفي ${suggestion.missingQty} أكثر للحصول على ${suggestion.getQty} ${getPublicId} ${discountText}`
      : `Add ${suggestion.missingQty} more to get ${suggestion.getQty} ${getPublicId} ${discountText}`;
  }

  return lang === "AR"
    ? `أضيفي ${suggestion.getQty} ${getPublicId} إلى السلة ${discountText}`
    : `Add ${suggestion.getQty} ${getPublicId} to cart ${discountText}`;
};

const getProductMainImage = () => {
  const first = product?.images?.[0];

  if (!first) return "/images/placeholder-product.jpg";
  if (typeof first === "string") return first;

  return first.url || first.image || first.image_url || "/images/placeholder-product.jpg";
};

const isModalVideo = (url = "") => {
  return /\.(mp4|webm|ogg|mov)$/i.test(String(url || "").split("?")[0]);
};

  const handleRateProduct = async (rating) => {
  if (!product?.id || ratingSaving) return;

  try {
    setSelectedRating(rating);
    setRatingSaving(true);
    setRatingMessage("");

    const result = await rateProduct(product.id, rating);

    setProduct((prev) =>
  prev
    ? {
        ...prev,
        rating_average: result.rating_average,
        rating_count: result.rating_count,
        rating_breakdown: result.rating_breakdown,
      }
    : prev
);

    setRatingMessage(
      lang === "AR"
        ? "شكراً لتقييمك"
        : "Thank you for your rating"
    );
  } catch (err) {
    setRatingMessage(
      lang === "AR"
        ? "تعذر حفظ التقييم"
        : "Could not save rating"
    );
  } finally {
    setRatingSaving(false);
  }
};

  const handleAddToBag = async () => {
    if (!selectedColor || !selectedVariant || selectedVariantStock <= 0) {
  setError(TEXT.selectVariant);
  return;
}

    setError("");
    setIsPlacing(true);

    if (mainImageRef.current) {
      flyImageToCart(mainImageRef.current);
    }

    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "add_to_cart",
        entity_type: "product",
        entity_id: product.id,
      }),
    }).catch(() => {});

    const safeQuantity = Math.min(
  Math.max(1, Number(quantity || 1)),
  selectedVariantStock
);

    const updatedCart = await addToCart(
  {
    product_id: product.id,
    variant_id: selectedVariant.id,
    bxgyDetails: product.bxgy_details || [],
    public_id: product.public_id,
    nameEN: product.name_en,
    nameAR: product.name_ar,
    price: finalPrice + (selectedVariant.extra_price || 0),
    image: product.images?.[0]?.url,
    color: selectedVariant.color_name,
    color_hex: selectedVariant.color_hex || "#000000",
    size: selectedVariant.size,
  },
  safeQuantity
);

const itemPrice = Number(finalPrice + (selectedVariant.extra_price || 0));
const addedImage = getProductMainImage();
const addedQty = Number(safeQuantity || 1);

const newCartCount =
  updatedCart?.totalItems ??
  ((Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + Number(item.qty || 0), 0)
    : 0) + addedQty);

const newSubtotal =
  updatedCart?.subtotal ??
  Number(subtotal || 0) + itemPrice * addedQty;

  let orderDiscount = null;

try {
  orderDiscount = await getActiveOrderDiscount();
} catch {}

const orderDiscountInfo = buildOrderDiscountInfo({
  discount: orderDiscount,
  subtotal: newSubtotal,
  totalItems: newCartCount,
  lang,
});

const bxgySuggestion =
  updatedCart?.bxgySuggestion ||
  updatedCart?.suggestion ||
  null;

setAddedModal({
  image: addedImage,
  isVideo: isModalVideo(addedImage),
  count: newCartCount,
  subtotal: newSubtotal,
  total: orderDiscountInfo.finalTotal,
  bxgySuggestion,
  freeShippingInfo: buildFreeShippingInfo({
    discount: freeShippingDiscount,
    subtotal: newSubtotal,
    totalItems: newCartCount,
    lang,
  }),
  orderDiscountInfo,
});

    setTimeout(() => setIsPlacing(false), 800);
  };

  const ratingCount = Number(product?.rating_count || 0);

const getRatingRowCount = (rating) => {
  const breakdown = product?.rating_breakdown || {};
  return Number(breakdown[rating] || breakdown[String(rating)] || 0);
};

const getRatingRowPercent = (rating) => {
  if (!ratingCount) return 0;
  return Math.round((getRatingRowCount(rating) / ratingCount) * 100);
};

const selectDesktopMedia = (index) => {
  if (!media.length) return;

  const safeIndex =
    index < 0 ? media.length - 1 : index >= media.length ? 0 : index;

  const direction =
    safeIndex === activeIndex
      ? mediaDirection
      : safeIndex > activeIndex
      ? "next"
      : "prev";

  setMediaDirection(direction);
  setActiveIndex(safeIndex);
  setViewerZoomed(false);
};

  /* ---------------- RENDER ---------------- */

  return (
  <div className="w-full min-h-screen flex flex-col overflow-x-hidden bg-[#F6EFE9]">
      <TopBanner />
      <MainHeader />
      <CartDrawer />

      <div
  className="w-full pt-[0px] px-4 pb-0"
  dir={lang === "AR" ? "rtl" : "ltr"}
>
        {/* MOBILE PRODUCT DETAILS LAYOUT */}
<div className="md:hidden bg-[#F6EFE9] -mx-4 px-4 pt-0 pb-4">
  <style>
  {`
    @keyframes mobileMainSwipeNext {
      from { opacity: 0.35; transform: translateX(22px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes mobileMainSwipePrev {
      from { opacity: 0.35; transform: translateX(-22px); }
      to { opacity: 1; transform: translateX(0); }
    }

    @keyframes mobileThumbSwipeNext {
      from { opacity: 0.45; transform: translateY(18px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes mobileThumbSwipePrev {
      from { opacity: 0.45; transform: translateY(-18px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `}
</style>
  {/* MOBILE BREADCRUMB */}
  <div className="relative mb-3 mt-0 overflow-hidden">
    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar whitespace-nowrap pr-12 text-[14px] text-black">
      <button
        type="button"
        onClick={() => navigate("/")}
        className="shrink-0"
      >
        {lang === "EN" ? "Main page" : "الصفحة الرئيسية"}
      </button>

      {(sourceCategoryFromUrl || principalCategory) && (
  <>
    <span className="shrink-0 text-[#555]">›</span>
    <button
      type="button"
      onClick={() => {
        const targetCategory = sourceCategoryFromUrl || principalCategory;
        navigate(`/products?category=${encodeURIComponent(targetCategory)}`);
      }}
      className="shrink-0"
    >
      {formatBreadcrumbLabel(sourceCategoryFromUrl || principalCategory)}
    </button>
  </>
)}

{sourceTagFromUrl && (
  <>
    <span className="shrink-0 text-[#555]">›</span>
    <button
      type="button"
      onClick={backToSourceCategory}
      className="shrink-0"
    >
      {formatBreadcrumbLabel(sourceTagFromUrl)}
    </button>
  </>
)}

      <span className="shrink-0 text-[#555]">›</span>

      <span className="shrink-0 font-medium text-black">
        {displayName}
      </span>
    </div>

    <div
      className={`
        pointer-events-none absolute top-0 bottom-0 w-14
        ${lang === "AR" ? "left-0" : "right-0"}
      `}
      style={{
        background:
          lang === "AR"
            ? "linear-gradient(to right, white, rgba(255,255,255,0))"
            : "linear-gradient(to left, white, rgba(255,255,255,0))",
      }}
    />
  </div>

  <div className="h-px bg-[#d9d9d9] mb-5" />

  {/* MOBILE MEDIA AREA */}
  <div className="grid grid-cols-[22%_1fr] gap-3 items-start">
    {/* THUMBNAILS */}
<div className="flex flex-col items-center justify-start">
  <button
    type="button"
    onClick={prevMedia}
    className="
      h-[24px] w-full
      flex items-center justify-center
      text-black
      active:scale-95
    "
    aria-label="Previous thumbnail"
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-[18px] h-[18px]"
      aria-hidden="true"
    >
      <path
        d="M6 15L12 9L18 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>

  <div
  key={`thumbs-${activeIndex}`}
  className={`
    flex flex-col gap-[6px] w-full
    transition-all duration-300 ease-out
    ${
      mediaDirection === "next"
        ? "animate-[mobileThumbSwipeNext_260ms_ease-out]"
        : "animate-[mobileThumbSwipePrev_260ms_ease-out]"
    }
  `}
>
    {visibleMobileThumbs.map((m, slotIndex) => {
      const realIndex = slotIndex === 0
        ? activeIndex
        : (activeIndex + 1) % media.length;

      const thumbUrl = getMediaUrl(m);
      const thumbIsVideo = isVideoMedia(m);
      const active = realIndex === activeIndex;

      return (
        <button
          key={`mobile-thumb-${slotIndex}-${realIndex}-${thumbUrl}`}
          type="button"
          onClick={() => {
  const direction = realIndex > activeIndex ? "next" : "prev";
  goToMedia(realIndex, direction);
}}
          className={`
            w-full
            aspect-[3/4]
            overflow-hidden
            bg-[#f6f6f6]
            border
            ${active ? "border-transparent" : "border-transparent"}
          `}
        >
          {thumbIsVideo ? (
  <video
    src={thumbUrl}
    muted
    playsInline
    preload="metadata"
    className="w-full h-full object-cover block"
  />
) : (
  <img
  src={thumbUrl}
  alt=""
  loading="lazy"
  decoding="async"
  className="w-full h-full object-cover"
/>
)}
        </button>
      );
    })}
  </div>

  <button
    type="button"
    onClick={nextMedia}
    className="
      h-[24px] w-full
      flex items-center justify-center
      text-black
      active:scale-95
    "
    aria-label="Next thumbnail"
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-[18px] h-[18px]"
      aria-hidden="true"
    >
      <path
        d="M6 9L12 15L18 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
</div>

    {/* MAIN IMAGE */}
    <div className="relative bg-[#f7f7f7] top-5 aspect-[4/5] overflow-hidden">
      {isVideo ? (
        <video
          key={activeMediaUrl}
          ref={mainImageRef}
          src={activeMediaUrl}
          controls
          muted
          playsInline
          preload="metadata"
          className={`
  w-full h-full object-cover bg-white
  ${
    mediaDirection === "next"
      ? "animate-[mobileMainSwipeNext_280ms_ease-out]"
      : "animate-[mobileMainSwipePrev_280ms_ease-out]"
  }
`}
        />
      ) : (
 <img
  key={activeMediaUrl}
  ref={mainImageRef}
  src={activeMediaUrl}
  alt={displayName}
  loading="eager"
  decoding="async"
  fetchPriority="high"
  onError={(event) => {
    if (event.currentTarget.dataset.fallbackApplied === "1") return;

    event.currentTarget.dataset.fallbackApplied = "1";
    event.currentTarget.src = "/assets/logo.jpg";
  }}
          className={`
  w-full h-full object-cover bg-white
  ${
    mediaDirection === "next"
      ? "animate-[mobileMainSwipeNext_280ms_ease-out]"
      : "animate-[mobileMainSwipePrev_280ms_ease-out]"
  }
`}
          onClick={() => {
            setViewerOpen(true);
            setViewerZoomed(false);
          }}
        />
      )}

      {media.length > 1 && (
        <>
          <button
            type="button"
            onClick={lang === "AR" ? nextMedia : prevMedia}
            className="
  absolute left-0 top-1/2 -translate-y-1/2
  w-[36px] h-[60px]
  bg-black/50
  text-white text-[26px]
  flex items-center justify-center
  active:scale-95
"
          >
            {lang === "AR" ? "›" : "‹"}
          </button>

          <button
            type="button"
            onClick={lang === "AR" ? prevMedia : nextMedia}
            className="
  absolute right-0 top-1/2 -translate-y-1/2
  w-[36px] h-[60px]
  bg-black/50
  text-white text-[26px]
  flex items-center justify-center
  active:scale-95
"
          >
            {lang === "AR" ? "‹" : "›"}
          </button>
        </>
      )}

      {!isVideo && (
        <button
  type="button"
  onClick={() => {
    setViewerOpen(true);
    setViewerZoomed(false);
  }}
  className="
    absolute right-3 bottom-3
    w-[46px] h-[46px]
    bg-white/75
    flex items-center justify-center
    shadow-[0_2px_8px_rgba(0,0,0,0.08)]
    active:scale-95
  "
  aria-label="Open image viewer"
>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-[29px] h-[29px] text-black"
    aria-hidden="true"
  >
    <circle
      cx="10.5"
      cy="10.5"
      r="5.8"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M15 15L20 20"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M10.5 7.8V13.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M7.8 10.5H13.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
</button>
      )}
    </div>
  </div>

  {/* PRODUCT TITLE */}
  <h1
    className="
      mt-9
      text-center
      text-[23px]
      leading-[1.28]
      font-light
      uppercase
      tracking-[0.05em]
      text-black
    "
  >
    {displayName}
  </h1>

 {/* PRICE */}
<div className="mt-5 flex flex-col items-center text-center">
  <div className="flex items-center justify-center gap-2">
    <span className="text-[32px] leading-none font-semibold text-black">
      {finalPrice}$
    </span>

    {hasDiscount && (
      <span
        className="
          inline-flex items-center justify-center
          w-[44px] h-[32px]
          bg-red-600 text-white
          text-[14px] font-bold
          leading-none
        "
      >
        -{discount}%
      </span>
    )}
  </div>

  {hasDiscount && (
    <span className="mt-2 text-[18px] line-through text-[#999]">
      {product.base_price}$
    </span>
  )}

  {bxgyLabel && (
    <div
      className="
        mt-4
        inline-flex
        items-center
        justify-center
        bg-green-700
        text-white
        px-4
        py-2
        text-[14px]
        font-semibold
        rounded-md
        text-center
      "
    >
      {bxgyLabel}
    </div>
  )}
</div>

  {/* COLOR IMAGE SELECT */}
  <div className="mt-10">
    <p className="text-[16px] font-semibold  uppercase text-black mb-0">
      {lang === "EN" ? "Select colour" : "اختاري اللون"}
    </p>

    <div className="h-px bg-[#cfcfcf] mb-3" />

    <div className="flex gap-3 flex-wrap">
      {colorImageOptions.map((c) => {
        const active = selectedColor === c.name;

        return (
          <button
            key={c.name}
            type="button"
            onClick={() => {
              if (selectedColor === c.name) return;

              setSelectedColor(c.name);
              setSelectedVariant(null);
              setQuantity(1);
              setError("");

              setColorInUrl(c.name);
              scrollToColorImage(c.name);
            }}
            className={`
  w-[68px] aspect-[3/4]
  border-[3px]
  bg-white
  overflow-hidden
  ${active ? "border-gray-300" : "border-transparent"}
`}
            title={c.name}
          >
            {c.imageUrl ? (
  c.isVideo ? (
    <video
      src={c.imageUrl}
      muted
      playsInline
      preload="metadata"
      className="w-full h-full object-cover block"
    />
  ) : (
    <img
  src={c.imageUrl}
  alt={c.name}
  loading="lazy"
  decoding="async"
  className="w-full h-full object-cover block"
/>
  )
) : (
  <span
    className="block w-full h-full"
    style={{ backgroundColor: c.code }}
  />
)}
          </button>
        );
      })}
    </div>
  </div>

  {/* SIZE */}
  {sizesByColor.length > 0 && (
    <div className="mt-12">
      <p className="text-[16px] uppercase font-semibold text-black mb-0">
        {TEXT.size}
      </p>

      <div className="h-px bg-[#cfcfcf] mb-3" />

      <div className="flex gap-3 flex-wrap">
        {sizesByColor.map((v) => {
          const active = selectedVariant?.id === v.id;
          const disabled = Number(v.stock_qty || 0) <= 0;

          return (
           <button
  key={v.id}
  type="button"
  disabled={disabled}
  onClick={() => {
    setSelectedVariant(v);
    setQuantity(1);
    setError("");
  }}
  className={`
    min-w-[43px] h-[43px]
    border-2 border-black
    bg-white
    uppercase
    text-[16px]
    flex items-center justify-center
    p-[4px]
    ${disabled ? "opacity-40 line-through" : "opacity-100"}
  `}
>
  <span
    className={`
      w-full h-full
      flex items-center justify-center
      ${active ? "bg-black text-white" : "bg-white text-black"}
    `}
  >
    {v.size}
  </span>
</button>
          );
        })}
      </div>
    </div>
  )}

  {/* QUANTITY */}
  <div className="mt-5 flex items-center gap-1">
    <button
  type="button"
  disabled={quantity <= 1}
  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
  className={`
    w-[46px] h-[42px]
    border border-[#d7d7d7]
    text-[28px] font-bold
    ${quantity <= 1 ? "opacity-40 cursor-not-allowed" : ""}
  `}
>
  -
</button>

    <div className="w-[64px] h-[42px] border border-[#d7d7d7] flex items-center justify-center text-[22px]">
      {quantity}
    </div>

    <button
  type="button"
  disabled={!quantityMax || quantity >= quantityMax}
  onClick={() =>
    setQuantity((q) => Math.min(quantityMax || 1, q + 1))
  }
  className={`
    w-[46px] h-[42px]
    border border-[#d7d7d7]
    text-[28px] font-bold
    ${!quantityMax || quantity >= quantityMax ? "opacity-40 cursor-not-allowed" : ""}
  `}
>
  +
</button>
  </div>

  {/* STOCK BADGE */}
  {stockBadgeText && (
    <div className="mt-3">
      <span
  className="
    inline-flex
    border-2 border-black
    px-4 py-3
    text-[17px]
    font-extrabold
    uppercase
    text-red-600
  "
>
  {stockBadgeText}
</span>
    </div>
  )}

  {error && (
    <p className="mt-3 text-[13px] text-red-600 font-medium">
      {error}
    </p>
  )}

  {/* ADD + HEART */}
  <div className="mt-5 flex items-center gap-2">
    <button
  type="button"
  onClick={handleAddToBag}
  disabled={!canAddToBag}
  className={`
    w-[72%]
    h-[64px]
    bg-black
    text-white
    text-[18px]
    uppercase
    tracking-[0.04em]
    ${!canAddToBag ? "opacity-45 cursor-not-allowed" : "opacity-100"}
  `}
>
  {TEXT.addToBag}
</button>

    <button
  type="button"
  onClick={() =>
    toggleWishlist({
      id: product.id,
      nameEN: product.name_en,
      nameAR: product.name_ar,
      price: finalPrice,
      image: product.images?.[0]?.url,
    })
  }
  className={`
    w-[62px] h-[62px]
    flex items-center justify-center
    text-[52px] leading-none
    transition
    ${inWishlist ? "text-red-600" : "text-black"}
  `}
  aria-label="Wishlist"
>
  {inWishlist ? "♥" : "♡"}
</button>
    </div>

  {/* MOBILE PRODUCT EXTRA DETAILS */}
  <div className="mt-12 text-black">
    {/* DIMENSIONS */}
    {String(dimensionsText || "").trim() && (
      <section className="mt-8">
        <div
  className="
    w-full
    flex items-center gap-2
    text-left
    text-[16px]
    font-extrabold
    uppercase
    text-black
    pb-2
    border-b border-[#cfcfcf]
  "
>
  <DimensionIcon />

  <span>
    {lang === "EN"
      ? "Product dimensions and details"
      : "أبعاد وتفاصيل المنتج"}
  </span>
</div>

        <div
          className="
            mt-4
            text-[15px]
            leading-[1.42]
            font-normal
            whitespace-pre-line
            text-black
          "
        >
          {dimensionsText}
        </div>
      </section>
    )}

    {/* CARE TIPS */}
    <section className="mt-10">
      <p className="text-[17px] underline mb-3">
        {lang === "EN" ? "Care tips:" : "نصائح العناية:"}
      </p>

      <div className="space-y-0 text-[15px] leading-[1.25]">
        <div className="flex items-center gap-3">
          <WashIcon />
          <span>{lang === "EN" ? "Hand wash" : "غسيل يدوي"}</span>
        </div>

        <div className="flex items-center gap-3">
          <IronIcon />
          <span>
            {lang === "EN"
              ? "Can be ironed at 110°C"
              : "يمكن الكي على حرارة 110°C"}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <CrossBoxIcon />
          <span>
            {lang === "EN"
              ? "Do not dry in the laundry dryer"
              : "لا تجفف في النشافة"}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <CrossCircleIcon />
          <span>
            {lang === "EN" ? "Do not dry clean" : "لا تنظف تنظيفاً جافاً"}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <BleachIcon />
          <span>
            {lang === "EN"
              ? "Cannot be bleached or chlorinated"
              : "لا يمكن تبييضه أو استخدام الكلور"}
          </span>
        </div>
      </div>
    </section>

    {/* PRODUCT DESCRIPTION */}
    {String(productDescriptionText || "").trim() && (
      <section className="mt-12">
        <h2
          className="
            text-[16px]
            font-semibold
            uppercase
            text-black
            pb-0
            border-b border-[#cfcfcf]
          "
        >
          {lang === "EN" ? "Product description" : "وصف المنتج"}
        </h2>
        <div
          className="
            mt-5
            text-[19px]
            leading-[1.35]
            font-normal
            whitespace-pre-line
            text-black
          "
        >
          {productDescriptionText}
        </div>
      </section>
    )}

    {/* DELIVERY + PICKUP INFO */}
<DeliveryBenefits />

    {/* RELATED TAGS */}
    {relatedTags.length > 0 && (
      <section className="mt-9">
        <h2
          className="
            text-[16px]
            font-semibold
            uppercase
            text-black
            pb-0
            border-b border-[#cfcfcf]
          "
        >
          {lang === "EN" ? "Related tags" : "وسوم مرتبطة"}
        </h2>

        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
          {relatedTags.map((tag) => (
            <button
              key={tag.slug}
              type="button"
              onClick={() => goToTag(tag.slug)}
              className="
                text-[18px]
                leading-[1.25]
                font-extrabold
                underline
                text-black
              "
            >
              {getRelatedTagLabel(tag)}
            </button>
          ))}
        </div>
      </section>
    )}

   {/* INSTAGRAM REELS */}
{productInstagramReels.length > 0 && (
  <section className="mt-16">
    <h2
      className="
        text-center
        text-[26px]
        leading-[1.05]
        font-extrabold
        tracking-[0.02em]
        text-black
      "
    >
      {productInstagramTitle}
    </h2>

    <div className="mt-2 h-px bg-[#d9d9d9]" />

    <div className="mt-6 grid grid-cols-2 gap-[8px]">
      {productInstagramReels.map((reel, index) => {
        const videoUrl = reel.video_url || reel.videoUrl || "";
        const reelUrl = reel.reel_url || reel.url || "";

        return (
          <a
            key={`${videoUrl}-${index}`}
            href={reelUrl || resolveInstagramVideo(videoUrl)}
            target="_blank"
            rel="noreferrer"
            className="
              relative
              block
              bg-[#f4f4f4]
              overflow-hidden
              aspect-[9/16]
            "
          >
            <video
  src={resolveInstagramVideo(videoUrl)}
  muted
  loop
  playsInline
  preload="none"
  className="
    w-full
    h-full
    object-cover
    block
  "
/>

            {/* Top Instagram icon */}
            <div
  className="
    absolute top-0 left-0 z-10
    w-[28px] h-[28px]
    bg-white
    flex items-center justify-center
    shadow-sm
    text-black
  "
>
  <InstagramIcon />
</div>

            {/* Likes label */}
            {String(reel.likes || "").trim() && (
              <div
                className="
                  absolute bottom-1.5 left-1.5 z-10
                  min-w-[64px]
                  h-[28px]
                  px-2
                  bg-white/95
                  flex items-center gap-1
                  text-[14px]
                  text-black
                "
              >
                <span className="text-red-600 flex items-center">
  <HeartIcon />
</span>
<span>{reel.likes}</span>
              </div>
            )}
          </a>
        );
      })}
    </div>
  </section>
)}

{/* PRODUCTS USED IN LOOK */}
{productLookProducts.length > 0 && (
  <section className="mt-12">
    <h2
      className="
        text-[16px]
        leading-[1.15]
        font-medium
        uppercase
        tracking-[0.01em]
        text-black
      "
    >
      {productLookTitle}
    </h2>

    <div className="mt-0 h-px bg-[#cfcfcf]" />

    <div className="relative mt-4">
      {productLookProducts.length > 2 && (
        <>
          <button
            type="button"
            onClick={() => scrollProductLook("prev")}
            className="
              absolute left-0 top-[38%] -translate-y-1/2 z-20
              w-9 h-12
              bg-white/90
              border border-[#d9d9d9]
              shadow-sm
              flex items-center justify-center
              text-[30px]
              leading-none
              text-black
              active:scale-95
            "
            aria-label="Previous products"
          >
            ‹
          </button>

          <button
            type="button"
            onClick={() => scrollProductLook("next")}
            className="
              absolute right-0 top-[38%] -translate-y-1/2 z-20
              w-9 h-12
              bg-white/90
              border border-[#d9d9d9]
              shadow-sm
              flex items-center justify-center
              text-[30px]
              leading-none
              text-black
              active:scale-95
            "
            aria-label="Next products"
          >
            ›
          </button>
        </>
      )}

      <div
        ref={productLookScrollRef}
        className="
          flex gap-[18px]
          overflow-x-auto
          no-scrollbar
          scroll-smooth
          pb-2
        "
      >
        {productLookProducts.map((p, index) => {
          const lookDiscount = Number(p.discount ?? p.sale_percent ?? 0);
const lookHasDiscount = lookDiscount > 0;
const lookFinalPrice = getLookProductPrice(p);
const lookOriginalPrice = Number(p.price ?? p.base_price ?? 0);
const lookName = getLookProductName(p);
const mediaUrl = getLookProductMedia(p);
const mediaIsVideo = isLookProductVideo(p);

          return (
            <button
  key={`look-product-${p.id || "no-id"}-${p.seo_slug || p.slug || "no-slug"}-${index}`}
  type="button"
  onClick={(e) => goToLookProduct(p, e)}
  data-product-card-id={p.id}
  className="
    shrink-0
    w-[calc(50%-9px)]
    min-h-[360px]
    bg-white
    text-center
    flex flex-col
  "
>
              <div className="w-full aspect-[3/4] bg-[#f5f5f5] overflow-hidden shrink-0">
                {mediaIsVideo ? (
                  <video
  src={mediaUrl}
  muted
  loop
  playsInline
  preload="none"
  className="w-full h-full object-cover block"
/>
                ) : (
                  <img
  src={mediaUrl}
  alt={lookName}
  loading="lazy"
  decoding="async"
  className="w-full h-full object-cover block"
/>
                )}
              </div>

              <div className="flex flex-col flex-1 px-1">
                <h3
                  className="
                    mt-5
                    min-h-[48px]
                    text-[20px]
                    leading-[1.2]
                    font-normal
                    text-black
                    line-clamp-2
                  "
                >
                  {lookName}
                </h3>

                <div className="mt-auto pt-5 flex items-center justify-center gap-2 min-h-[44px]">
                  <span className="text-[18px] text-black">
                    ${lookFinalPrice}
                  </span>

                  {lookHasDiscount && (
                    <span className="text-[14px] text-[#999] line-through">
                      ${lookOriginalPrice}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  </section>
)}

{/* CUSTOMERS REVIEWS */}
<section className="mt-10">
  <h2
    className="
      text-[32px]
      leading-[1.08]
      font-medium
      text-black
    "
  >
    {lang === "AR" ? "آراء العملاء" : "Customers' reviews"}
  </h2>

  <div className="mt-4 bg-[#F6EFE9] px-6 py-5">
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map((star) => {
        const active =
          star <= (hoverRating || selectedRating || Math.round(Number(product?.rating_average || 0)));

        return (
          <button
            key={star}
            type="button"
            disabled={ratingSaving}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            onClick={() => handleRateProduct(star)}
            className={`
              text-[44px]
              leading-none
              transition
              active:scale-95
              ${active ? "text-yellow-400" : "text-[#e5e5e5]"}
              ${ratingSaving ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
            `}
            aria-label={`Rate ${star} stars`}
          >
            ★
          </button>
        );
      })}
    </div>

    {ratingCount === 0 && (
  <p className="mt-7 text-[22px] leading-[1.35] text-black">
    {lang === "AR"
      ? "نحن بانتظار أول تقييم"
      : "We are waiting for the first rating"}
  </p>
)}

    <div className="mt-8 space-y-5">
  {[5, 4, 3, 2, 1].map((row) => {
    const rowCount = getRatingRowCount(row);
    const rowPercent = getRatingRowPercent(row);

    return (
      <div key={row} className="flex items-center gap-2">
        <span className="w-5 text-[18px] text-gray-700">{row}</span>

        <span
          className={`
            text-[26px] leading-none
            ${rowCount > 0 ? "text-yellow-400" : "text-[#e5e5e5]"}
          `}
        >
          ★
        </span>

        <div className="h-[8px] flex-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`
              h-full transition-all duration-300
              ${rowCount > 0 ? "bg-yellow-400" : "bg-[#e5e5e5]"}
            `}
            style={{ width: `${rowPercent}%` }}
          />
        </div>
      </div>
    );
  })}
</div>

    {ratingMessage && (
      <p className="mt-6 text-[15px] text-black">
        {ratingMessage}
      </p>
    )}
  </div>
</section>

  </div>
</div>

        <button
          onClick={() => navigate(-1)}
          className="
  hidden md:block
  absolute top-[110px]
  text-3xl text-[#2E3135]
  opacity-60 hover:opacity-100
"
          style={lang === "AR" ? { right: "16px" } : { left: "16px" }}
        >
          ×
        </button>

       {/* DESKTOP PRODUCT DETAILS LAYOUT - ANDZELA STYLE */}
<div className="hidden md:block w-full bg-[#F6EFE9] mt-[165px]">
  <style>
    {`
      @keyframes desktopMainSwipeNext {
        from { opacity: 0.45; transform: translateX(28px); }
        to { opacity: 1; transform: translateX(0); }
      }

      @keyframes desktopMainSwipePrev {
        from { opacity: 0.45; transform: translateX(-28px); }
        to { opacity: 1; transform: translateX(0); }
      }

      @keyframes desktopThumbSwipeNext {
        from { opacity: 0.45; transform: translateY(18px); }
        to { opacity: 1; transform: translateY(0); }
      }

      @keyframes desktopThumbSwipePrev {
        from { opacity: 0.45; transform: translateY(-18px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}
  </style>
  {/* DESKTOP BREADCRUMB */}
  <div className="w-full">
  <div className="w-full mx-0 px-[1px] py-3">
      <div className="flex items-center gap-2 text-[16px] text-[#555]">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="hover:underline"
        >
          {lang === "EN" ? "Main page" : "الصفحة الرئيسية"}
        </button>

        {(sourceCategoryFromUrl || principalCategory) && (
          <>
            <span>›</span>
            <button
              type="button"
              onClick={() => {
                const targetCategory = sourceCategoryFromUrl || principalCategory;
                navigate(`/products?category=${encodeURIComponent(targetCategory)}`);
              }}
              className="hover:underline"
            >
              {formatBreadcrumbLabel(sourceCategoryFromUrl || principalCategory)}
            </button>
          </>
        )}

        <span>›</span>

        <span className="text-[#222]">
          {displayName}
        </span>
      </div>
    </div>
    <div className="w-[60%] mx-auto h-px bg-[#d8d8d8]" />
  </div>

  {/* TOP PRODUCT AREA */}
  <div
  className="
    max-w-none
    w-full
    mx-0
    px-[1px]
    pt-8
    grid
    grid-cols-[118px_minmax(0,1fr)_390px]
    gap-2
    items-start
  "
>
    {/* LEFT THUMBNAILS */}
<aside className="sticky top-[125px] self-start -mt-6 flex flex-col items-center">
  <button
    type="button"
    onClick={() => {
  setMediaDirection("prev");
  selectDesktopMedia(activeIndex - 1);
}}
    className="
      h-[24px] w-full
      flex items-center justify-center
      text-black
      active:scale-95
    "
    aria-label="Previous thumbnail"
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-[18px] h-[18px]"
      aria-hidden="true"
    >
      <path
        d="M6 15L12 9L18 15"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>

  <div
  key={`desktop-thumbs-${activeIndex}`}
  className={`
    flex flex-col gap-[3px] w-full overflow-hidden
    transition-all duration-300 ease-out
    ${
      mediaDirection === "next"
        ? "animate-[desktopThumbSwipeNext_260ms_ease-out]"
        : "animate-[desktopThumbSwipePrev_260ms_ease-out]"
    }
  `}
>
  {desktopVisibleThumbs.map(({ item: m, index: realIndex }) => {
    const thumbUrl = getMediaUrl(m);
    const thumbIsVideo = isVideoMedia(m);

    return (
      <button
        key={`desktop-thumb-${realIndex}-${thumbUrl}`}
        type="button"
        onClick={() => selectDesktopMedia(realIndex)}
        className="
          w-[112px]
          h-[158px]
          bg-[#f6f6f6]
          overflow-hidden
          border border-transparent
        "
      >
        {thumbIsVideo ? (
          <video
            src={thumbUrl}
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover block"
          />
        ) : (
          <img
            src={thumbUrl}
            alt=""
            className="w-full h-full object-cover block"
          />
        )}
      </button>
    );
  })}
</div>

  <button
    type="button"
    onClick={() => {
  setMediaDirection("next");
  selectDesktopMedia(activeIndex + 1);
}}
    className="
      h-[24px] w-full
      flex items-center justify-center
      text-black
      active:scale-95
    "
    aria-label="Next thumbnail"
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-[18px] h-[18px]"
      aria-hidden="true"
    >
      <path
        d="M6 9L12 15L18 9"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
</aside>

    {/* CENTER IMAGES GRID */}
<main className="min-w-0">
  <div className="grid grid-cols-2 gap-0 bg-white relative overflow-hidden">
    {desktopVisibleMedia.map((m, slotIndex) => {
      const realIndex =
        slotIndex === 0
          ? activeIndex
          : (activeIndex + 1) % media.length;

      const mediaUrl = getMediaUrl(m);
      const mediaIsVideo = isVideoMedia(m);

      return (
        <div
          key={`desktop-main-media-${slotIndex}-${realIndex}-${mediaUrl}`}
          ref={(el) => {
            desktopMediaRefs.current[realIndex] = el;
          }}
          className="
            relative
            aspect-[3/4]
            bg-[#f7f7f7]
            overflow-hidden
            group
          "
        >
          {mediaIsVideo ? (
            <video
              src={mediaUrl}
              controls
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover bg-white"
            />
          ) : (
            <img
              src={mediaUrl}
              alt={displayName}
              loading={slotIndex === 0 ? "eager" : "lazy"}
              decoding="async"
              onClick={() => {
                setActiveIndex(realIndex);
                setViewerOpen(true);
                setViewerZoomed(false);
              }}
              className={`
  w-full
  h-full
  object-cover
  bg-white
  cursor-pointer
  ${
    mediaDirection === "next"
      ? "animate-[desktopMainSwipeNext_280ms_ease-out]"
      : "animate-[desktopMainSwipePrev_280ms_ease-out]"
  }
`}
            />
          )}
        </div>
      );
    })}
   {media.length > 1 && (
  <>
    <button
      type="button"
      onClick={() => {
        setMediaDirection("prev");
        selectDesktopMedia(activeIndex - 1);
      }}
      className="
        absolute left-0 top-1/2 -translate-y-1/2 z-20
        w-[36px] h-[60px]
        bg-black/50
        text-white text-[26px]
        flex items-center justify-center
        active:scale-95
      "
      aria-label="Previous image"
    >
      ‹
    </button>

    <button
      type="button"
      onClick={() => {
        setMediaDirection("next");
        selectDesktopMedia(activeIndex + 1);
      }}
      className="
        absolute right-0 top-1/2 -translate-y-1/2 z-20
        w-[36px] h-[60px]
        bg-black/50
        text-white text-[26px]
        flex items-center justify-center
        active:scale-95
      "
      aria-label="Next image"
    >
      ›
    </button>
  </>
)} 
  </div>
</main>

    {/* RIGHT PRODUCT INFO */}
    <aside className="sticky top-[135px] self-start bg-[#F6EFE9] pb-10">
      <div className="pl-2">
        <h1
          className="
            text-center
            text-[22px]
            leading-[1.25]
            font-light
            uppercase
            tracking-[0.04em]
            text-[#333]
          "
        >
          {displayName}
        </h1>

        <div className="mt-5 text-center">
  <div className="flex items-center justify-center gap-2">
    <span className="text-[24px] leading-none font-normal text-black">
      {Number(finalPrice).toFixed(2)} $
    </span>

    {hasDiscount && (
      <span
        className="
          bg-[#b92025]
          text-white
          text-[11px]
          px-1.5
          py-1
          leading-none
        "
      >
        -{discount}%
      </span>
    )}
  </div>

  {hasDiscount && (
    <div className="mt-2 text-[13px] text-[#888]">
      <span className="line-through">
        {Number(product.base_price).toFixed(2)} $
      </span>
    </div>
  )}

  {bxgyLabel && (
    <div
      className="
        mt-4
        inline-flex
        items-center
        justify-center
        bg-green-700
        text-white
        px-3
        py-2
        text-[13px]
        font-semibold
        rounded-md
        text-center
      "
    >
      {bxgyLabel}
    </div>
  )}
</div>

        {/* COLOR */}
        <div className="mt-10">
  <p className="text-[16px] font-semibold uppercase text-black pb-2 mb-3 border-b border-[#cfcfcf]">
            {lang === "EN" ? "Select colour" : "اختاري اللون"}
          </p>

          <div className="flex gap-2 flex-wrap">
            {colorImageOptions.map((c) => {
              const active = selectedColor === c.name;

              return (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => {
                    if (selectedColor === c.name) return;

                    setSelectedColor(c.name);
                    setSelectedVariant(null);
                    setQuantity(1);
                    setError("");

                    setColorInUrl(c.name);
                    scrollToColorImage(c.name);

                    if (c.imageIndex !== undefined) {
                      selectDesktopMedia(c.imageIndex);
                    }
                  }}
                  title={c.name}
                  className={`
                    w-[54px]
                    h-[74px]
                    bg-[#f7f7f7]
                    overflow-hidden
                    border
                    transition
                    ${active ? "border-black" : "border-transparent hover:border-[#bdbdbd]"}
                  `}
                >
                  {c.imageUrl ? (
                    c.isVideo ? (
                      <video
                        src={c.imageUrl}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover block"
                      />
                    ) : (
                      <img
  src={c.imageUrl}
  alt={c.name}
  loading="lazy"
  decoding="async"
  className="w-full h-full object-cover block"
/>
                    )
                  ) : (
                    <span
                      className="block w-full h-full"
                      style={{ backgroundColor: c.code }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* SIZE */}
        {sizesByColor.length > 0 && (
          <div className="mt-6">
  <p className="text-[16px] font-semibold uppercase text-black pb-2 mb-3 border-b border-[#cfcfcf]">
              {TEXT.size}
            </p>

            <div className="flex gap-2 flex-wrap">
              {sizesByColor.map((v) => {
                const active = selectedVariant?.id === v.id;
                const disabled = Number(v.stock_qty || 0) <= 0;

                return (
                  <button
  key={v.id}
  type="button"
  disabled={disabled}
  onClick={() => {
    setSelectedVariant(v);
    setQuantity(1);
    setError("");
  }}
  className={`
    min-w-[43px] h-[43px]
    border-2 border-black
    bg-white
    uppercase
    text-[16px]
    flex items-center justify-center
    p-[4px]
    ${disabled ? "opacity-40 line-through" : "opacity-100"}
  `}
>
  <span
    className={`
      w-full h-full
      flex items-center justify-center
      ${active ? "bg-black text-white" : "bg-white text-black"}
    `}
  >
    {v.size}
  </span>
</button>
                );
              })}
            </div>
          </div>
        )}

        {/* QUANTITY */}
<div className="mt-5 flex items-center gap-1">
  <button
    type="button"
    disabled={quantity <= 1}
    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
    className={`
      w-[46px] h-[42px]
      border border-[#d7d7d7]
      text-[28px] font-bold
      ${quantity <= 1 ? "opacity-40 cursor-not-allowed" : ""}
    `}
  >
    -
  </button>

  <div className="w-[64px] h-[42px] border border-[#d7d7d7] flex items-center justify-center text-[22px]">
    {quantity}
  </div>

  <button
    type="button"
    disabled={!quantityMax || quantity >= quantityMax}
    onClick={() =>
      setQuantity((q) => Math.min(quantityMax || 1, q + 1))
    }
    className={`
      w-[46px] h-[42px]
      border border-[#d7d7d7]
      text-[28px] font-bold
      ${!quantityMax || quantity >= quantityMax ? "opacity-40 cursor-not-allowed" : ""}
    `}
  >
    +
  </button>
</div>

{/* DESKTOP STOCK BADGE */}
{stockBadgeText && (
  <div className="mt-3">
    <span
      className="
        inline-flex
        border-2 border-black
        px-4 py-3
        text-[15px]
        font-extrabold
        uppercase
        text-red-600
      "
    >
      {stockBadgeText}
    </span>
  </div>
)}

{error && (
  <p className="mt-3 text-[12px] text-red-600">
    {error}
  </p>
)}

        {/* ADD + WISHLIST */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handleAddToBag}
            disabled={!canAddToBag}
            className={`
  w-[72%]
  h-[64px]
  bg-black
  text-white
  text-[18px]
  uppercase
  tracking-[0.04em]
  ${!canAddToBag ? "opacity-45 cursor-not-allowed" : "opacity-100"}
`}
          >
            {TEXT.addToBag}
          </button>

          <button
            type="button"
            onClick={() =>
              toggleWishlist({
                id: product.id,
                nameEN: product.name_en,
                nameAR: product.name_ar,
                price: finalPrice,
                image: product.images?.[0]?.url,
              })
            }
            className={`
  w-[62px] h-[62px]
  flex items-center justify-center
  text-[52px] leading-none
  transition
  ${inWishlist ? "text-red-600" : "text-black"}
`}
            aria-label="Wishlist"
          >
            {inWishlist ? "♥" : "♡"}
          </button>
        </div>

        {/* PRODUCT DIMENSIONS */}
        {String(dimensionsText || "").trim() && (
          <section className="mt-7">
            <button
  type="button"
  className="
    w-full
    flex items-center justify-between
    text-left
    text-[16px]
    font-extrabold
    uppercase
    text-black
    pb-2
    border-b border-[#cfcfcf]
  "
>
              <span className="flex items-center gap-2">
                <DimensionIcon />
                {lang === "EN"
                  ? "Product dimensions and details"
                  : "أبعاد وتفاصيل المنتج"}
              </span>

              <span className="text-[12px]">▼</span>
            </button>

            <div
              className="
                mt-5
                text-[13px]
                leading-[1.55]
                font-normal
                whitespace-pre-line
                text-[#444]
              "
            >
              {dimensionsText}
            </div>
          </section>
        )}

        {/* PRODUCT DESCRIPTION */}
        {String(productDescriptionText || "").trim() && (
          <section className="mt-7">
            <h2 className="text-[16px] font-semibold uppercase text-black pb-2 border-b border-[#cfcfcf]">
  {lang === "EN" ? "Product description" : "وصف المنتج"}
</h2>

            <div
              className="
                mt-4
                text-[13px]
                leading-[1.55]
                whitespace-pre-line
                text-[#444]
              "
            >
              {productDescriptionText}
            </div>
          </section>
        )}

        {/* DELIVERY ICONS */}
<DeliveryBenefits compact />

        {/* RELATED TAGS */}
        {relatedTags.length > 0 && (
          <section className="mt-7">
            <h2 className="text-[16px] font-semibold uppercase text-black pb-2 border-b border-[#cfcfcf]">
  {lang === "EN" ? "Related tags" : "وسوم مرتبطة"}
</h2>

            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-2">
              {relatedTags.map((tag) => (
                <button
                  key={tag.slug}
                  type="button"
                  onClick={() => goToTag(tag.slug)}
                  className="
  text-[16px]
  leading-[1.25]
  font-extrabold
  underline
  text-black
  hover:text-[#555]
"
                >
                  {getRelatedTagLabel(tag)}
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </aside>
  </div>

  {/* DESKTOP PRODUCTS USED IN LOOK */}
  {productLookProducts.length > 0 && (
    <section className="max-w-[1400px] mx-auto px-6 mt-12">
      <h2
        className="
          text-[14px]
          leading-[1.15]
          font-medium
          uppercase
          tracking-[0.01em]
          text-black
        "
      >
        {productLookTitle}
      </h2>

      <div className="mt-2 h-px bg-[#cfcfcf]" />

      <div className="mt-6 flex justify-center">
        <div className="flex gap-8 overflow-x-auto no-scrollbar pb-4">
          {productLookProducts.map((p, index) => {
            const lookDiscount = Number(p.discount ?? p.sale_percent ?? 0);
            const lookHasDiscount = lookDiscount > 0;
            const lookFinalPrice = getLookProductPrice(p);
            const lookOriginalPrice = Number(p.price ?? p.base_price ?? 0);
            const lookName = getLookProductName(p);
            const mediaUrl = getLookProductMedia(p);
            const mediaIsVideo = isLookProductVideo(p);

            return (
              <button
  key={`desktop-look-product-${p.id || "no-id"}-${p.seo_slug || p.slug || "no-slug"}-${index}`}
  type="button"
  onClick={(e) => goToLookProduct(p, e)}
  data-product-card-id={p.id}
  className="
    shrink-0
    w-[250px]
    bg-white
    text-center
    flex
    flex-col
  "
>
                <div className="w-full aspect-[3/4] bg-[#f5f5f5] overflow-hidden">
                  {mediaIsVideo ? (
                    <video
  src={mediaUrl}
  muted
  loop
  playsInline
  preload="none"
  className="w-full h-full object-cover block"
/>
                  ) : (
                    <img
  src={mediaUrl}
  alt={lookName}
  loading="lazy"
  decoding="async"
  className="w-full h-full object-cover block"
/>
                  )}
                </div>

                <h3
                  className="
                    mt-3
                    min-h-[40px]
                    text-[15px]
                    leading-[1.25]
                    font-normal
                    text-black
                    line-clamp-2
                  "
                >
                  {lookName}
                </h3>

                <div className="mt-2 flex items-center justify-center gap-2">
                  <span className="text-[14px] text-black">
                    ${lookFinalPrice}
                  </span>

                  {lookHasDiscount && (
                    <span className="text-[12px] text-[#999] line-through">
                      ${lookOriginalPrice}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  )}

  {/* DESKTOP CUSTOMERS REVIEWS */}
<section className="max-w-[1180px] mx-auto px-6 mt-20 mb-24">
    <h2
      className="
        text-[42px]
        leading-[1.1]
        font-medium
        text-black
      "
    >
      {lang === "AR" ? "آراء العملاء" : "Customers' reviews"}
    </h2>

    <div className="mt-8 bg-[#F6EFE9] min-h-[330px] grid grid-cols-[420px_1fr] gap-12 px-12 py-12">
      <div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => {
            const active =
              star <=
              (hoverRating ||
                selectedRating ||
                Math.round(Number(product?.rating_average || 0)));

            return (
              <button
                key={`desktop-review-star-${star}`}
                type="button"
                disabled={ratingSaving}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => handleRateProduct(star)}
                className={`
                  text-[54px]
                  leading-none
                  transition
                  active:scale-95
                  ${active ? "text-yellow-400" : "text-[#e5e5e5]"}
                  ${ratingSaving ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                `}
                aria-label={`Rate ${star} stars`}
              >
                ★
              </button>
            );
          })}
        </div>

        {ratingCount === 0 && (
          <p className="mt-5 text-[13px] leading-[1.35] text-black">
            {lang === "AR"
              ? "نحن بانتظار أول تقييم"
              : "We are waiting for the first rating"}
          </p>
        )}

        <div className="mt-8 space-y-5">
          {[5, 4, 3, 2, 1].map((row) => {
            const rowCount = getRatingRowCount(row);
            const rowPercent = getRatingRowPercent(row);

            return (
              <div key={`desktop-rating-row-${row}`} className="flex items-center gap-2">
                <span className="w-5 text-[22px] text-gray-700">{row}</span>

                <span
                  className={`
                    text-[30px] leading-none
                    ${rowCount > 0 ? "text-yellow-400" : "text-[#e5e5e5]"}
                  `}
                >
                  ★
                </span>

                <div className="h-[9px] flex-1 bg-white rounded-full overflow-hidden">
                  <div
                    className={`
                      h-full transition-all duration-300
                      ${rowCount > 0 ? "bg-yellow-400" : "bg-[#e5e5e5]"}
                    `}
                    style={{ width: `${rowPercent}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center text-center">
        {ratingCount === 0 && (
  <p className="text-[16px] text-[#444]">
    {lang === "AR"
      ? "لا توجد تقييمات لهذا المنتج بعد."
      : "This product has no ratings or reviews yet."}
  </p>
)}

        {ratingMessage && (
          <p className="mt-4 text-[13px] text-black">
            {ratingMessage}
          </p>
        )}
      </div>
    </div>
  </section>
</div>

      </div>

{/* PRODUCT DETAILS ADDED TO CART MODAL */}
{addedModal && (
  <div
    className="
      fixed inset-0 z-[999999]
      bg-black/35
      flex items-center justify-center
      px-0 md:px-4
    "
    onClick={() => setAddedModal(null)}
  >
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      onClick={(e) => e.stopPropagation()}
      className="
        w-full
        md:max-w-[520px]
        bg-white
        rounded-t-md md:rounded-md
        shadow-[0_18px_55px_rgba(0,0,0,0.25)]
        border border-gray-200
        overflow-hidden
      "
    >
      {/* MOBILE HEADER */}
      <div className="md:hidden relative px-10 py-3 bg-white">
        <h3 className="text-[20px] leading-[1.35] font-semibold text-black">
          {lang === "AR"
            ? "تمت إضافة المنتج إلى سلة التسوق"
            : "Product successfully added to your shopping cart"}
        </h3>

        <button
          type="button"
          onClick={() => setAddedModal(null)}
          className="
            absolute top-2 right-2
            w-5 h-5
            rounded-full
            bg-gray-500
            text-white text-semibold
            flex items-center justify-center
            text-[22px] leading-none
            font-bold
            active:scale-95
          "
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* DESKTOP HEADER - same style as ProductCard modal */}
      <div className="hidden md:block relative px-5 py-4">
        <h3 className="text-[20px] font-normal text-gray-900">
          {lang === "AR" ? "تمت الإضافة إلى السلة" : "Added to cart"}
        </h3>

        <button
          type="button"
          onClick={() => setAddedModal(null)}
          className="
            absolute top-3 right-3
            w-6 h-6
            rounded-full
            bg-gray-500
            text-white
            flex items-center justify-center
            text-[16px] leading-none
            hover:bg-gray-700
            transition
          "
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* MOBILE BODY */}
      <div className="md:hidden bg-[#f3f3f3] px-6 py-3 flex flex-col items-center">
        <div className="bg-white border border-gray-300 p-1.5">
          {addedModal.isVideo ? (
            <video
              src={addedModal.image}
              muted
              playsInline
              preload="metadata"
              className="w-[110px] h-[160px] object-cover"
            />
          ) : (
            <img
              src={addedModal.image}
              alt=""
              className="w-[110px] h-[160px] object-cover"
            />
          )}
        </div>

        {addedModal.freeShippingInfo?.hasDiscount && (
  <div
    className={`
      mt-3
      px-3 py-2
      rounded-md
      text-white
      text-[15px]
      font-bold
      text-center
      ${
        addedModal.freeShippingInfo.valid
          ? "bg-[#10b981]"
          : "bg-[#f59e0b]"
      }
    `}
  >
    {addedModal.freeShippingInfo.label}

    {addedModal.freeShippingInfo.endLabel && (
      <div className="mt-1 text-[12px] font-medium opacity-90">
        {lang === "AR"
          ? `ينتهي في ${addedModal.freeShippingInfo.endLabel}`
          : `Ends on ${addedModal.freeShippingInfo.endLabel}`}
      </div>
    )}
  </div>
)}

{addedModal.orderDiscountInfo?.hasDiscount && (
  <div
    className={`
      mt-3
      px-3 py-2
      rounded-md
      text-white
      text-[15px]
      font-bold
      text-center
      ${
        addedModal.orderDiscountInfo.valid
          ? "bg-[#2563eb]"
          : "bg-[#f59e0b]"
      }
    `}
  >
    {addedModal.orderDiscountInfo.label}

    {addedModal.orderDiscountInfo.endLabel && (
      <div className="mt-1 text-[12px] font-medium opacity-90">
        {lang === "AR"
          ? `ينتهي في ${addedModal.orderDiscountInfo.endLabel}`
          : `Ends on ${addedModal.orderDiscountInfo.endLabel}`}
      </div>
    )}
  </div>
)}
      </div>

      {/* DESKTOP BODY - same style as ProductCard modal */}
      <div className="hidden md:block bg-[#f3f3f3] px-5 py-5">
        <div className="grid grid-cols-[110px_1fr] gap-6 items-center">
          <div className="bg-white border border-gray-300 p-1">
            {addedModal.isVideo ? (
              <video
                src={addedModal.image}
                muted
                playsInline
                preload="metadata"
                className="w-full aspect-square object-cover"
              />
            ) : (
              <img
                src={addedModal.image}
                alt=""
                className="w-full aspect-square object-cover"
              />
            )}
          </div>

          <div className="text-[13px] text-gray-800 space-y-2">
            <div className="flex justify-between gap-4">
              <span>
                {lang === "AR"
                  ? "عدد المنتجات في سلتك"
                  : "Number of products in your cart"}
              </span>
              <span className="font-semibold">{addedModal.count}</span>
            </div>

           <div className="flex justify-between gap-4">
  <span>{lang === "AR" ? "مجموع المنتجات" : "Total products"}</span>
  <span className="font-semibold">
    {formatModalPrice(addedModal.subtotal)}
  </span>
</div>

{addedModal.orderDiscountInfo?.valid &&
  Number(addedModal.orderDiscountInfo.discountAmount || 0) > 0 && (
    <div className="flex justify-between gap-4 text-green-700">
      <span>{lang === "AR" ? "خصم الطلب" : "Order discount"}</span>
      <span className="font-semibold">
        -{formatModalPrice(addedModal.orderDiscountInfo.discountAmount)}
      </span>
    </div>
)}

<div className="flex justify-between gap-4">
  <span>{lang === "AR" ? "الإجمالي" : "Together"}</span>
  <span className="font-semibold">
    {formatModalPrice(addedModal.total)}
  </span>
</div>

            {addedModal.freeShippingInfo?.hasDiscount && (
  <div
    className={`
      mt-2
      text-white
      px-3 py-2
      text-[12px]
      font-semibold
      ${
        addedModal.freeShippingInfo.valid
          ? "bg-[#10b981]"
          : "bg-[#f59e0b]"
      }
    `}
  >
    {addedModal.freeShippingInfo.label}

    {addedModal.freeShippingInfo.endLabel && (
      <div className="mt-1 text-[11px] font-medium opacity-90">
        {lang === "AR"
          ? `ينتهي في ${addedModal.freeShippingInfo.endLabel}`
          : `Ends on ${addedModal.freeShippingInfo.endLabel}`}
      </div>
    )}
  </div>
)}

{addedModal.orderDiscountInfo?.hasDiscount && (
  <div
    className={`
      mt-2
      text-white
      px-3 py-2
      text-[12px]
      font-semibold
      ${
        addedModal.orderDiscountInfo.valid
          ? "bg-[#2563eb]"
          : "bg-[#f59e0b]"
      }
    `}
  >
    {addedModal.orderDiscountInfo.label}

    {addedModal.orderDiscountInfo.endLabel && (
      <div className="mt-1 text-[11px] font-medium opacity-90">
        {lang === "AR"
          ? `ينتهي في ${addedModal.orderDiscountInfo.endLabel}`
          : `Ends on ${addedModal.orderDiscountInfo.endLabel}`}
      </div>
    )}
  </div>
)}

          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="px-10 md:px-5 py-5 md:py-5 bg-white">
        {addedModal?.bxgySuggestion && (
  <button
    type="button"
    onClick={async () => {
      await addBxgyDiscountItem(addedModal.bxgySuggestion);
      setAddedModal(null);
    }}
    className="
      mb-4
      w-full
      h-[50px] md:h-11
      bg-green-700
      text-white
      text-[15px] md:text-[13px]
      font-semibold
      rounded-md
      hover:bg-green-800
      transition
    "
  >
    {getBxgyUpsellText(addedModal.bxgySuggestion)}
  </button>
)}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-3">
          <button
            type="button"
            onClick={() => setAddedModal(null)}
            className="
              w-full md:flex-1
              h-[50px] md:h-11
              border-[3px] md:border
              border-black md:border-gray-700
              bg-white
              text-black md:text-gray-700
              text-[18px] md:text-[13px]
              font-normal
              hover:bg-gray-50
              transition
            "
          >
            {lang === "AR" ? "متابعة التسوق" : "Continue shopping"}
          </button>

          <button
            type="button"
            onClick={() => {
              apiRequest("/analytics/track", {
                method: "POST",
                body: JSON.stringify({
                  action_type: "open_cart_page",
                  entity_type: "cart",
                }),
              }).catch(() => {});

              setAddedModal(null);
              navigate("/cart");
            }}
            className="
              w-full md:flex-1
              h-[50px] md:h-11
              bg-[#f6a000] md:bg-[#e5ad20]
              text-white
              text-[18px] md:text-[13px]
              font-normal md:font-medium
              hover:bg-[#d59d17]
              transition
            "
          >
            {lang === "AR" ? "اذهب إلى الدفع »" : "Go to checkout »"}
          </button>
        </div>

        <p className="mt-5 md:mt-5 text-[14px] md:text-[13px] text-black md:text-gray-600 leading-relaxed">
          {lang === "AR"
            ? "لا تؤجل عملية الشراء، إضافة المنتجات إلى السلة لا تعني أنها محجوزة لك."
            : "Don't wait with your purchase, adding products to your bag does not mean that they are reserved."}
        </p>
      </div>
    </div>
  </div>
)}

{bxgyPreviewOpen && getItem?.image && (
  <div
    onClick={() => setBxgyPreviewOpen(false)}
    className="
      fixed inset-0 z-[10000]
      bg-black/80 flex items-center justify-center
    "
  >
    <div
      className="relative"
      onClick={(e) => e.stopPropagation()}
    >
      {/* IMAGE */}
      <img
        src={getItem.image}
        className="
          max-w-[90vw] max-h-[80vh]
          rounded-2xl shadow-2xl
        "
      />

      {/* DISCOUNT LABEL */}
      <div
  className="
    absolute top-3 left-3
    px-4 py-2 rounded-full
    text-white text-xs font-bold
    shadow-lg
  "
  style={{
    background:
      (getItem?.discount_type || getItem?.get_discount_type) === "free"
        ? "linear-gradient(135deg,#43A047,#66BB6A)"
        : "linear-gradient(135deg,#E53935,#FB8C00)",
  }}
>
  {(getItem?.discount_type || getItem?.get_discount_type) === "free"
    ? lang === "AR"
      ? "احصل عليه مجاناً 🎁"
      : "Get this FREE 🎁"
    : lang === "AR"
    ? `خصم ${getItem?.discount_value ?? getItem?.get_discount_value ?? 0}%`
    : `Get ${getItem?.discount_value ?? getItem?.get_discount_value ?? 0}% OFF`}
</div>

      {/* CLOSE BUTTON */}
      <button
        onClick={() => setBxgyPreviewOpen(false)}
        className="
          absolute -top-3 -right-3
          w-8 h-8 rounded-full
          bg-white text-black text-lg
          shadow-md
        "
      >
        ×
      </button>
    </div>
  </div>
)}

      {/* FULLSCREEN VIEWER */}
{viewerOpen && (
  <div
    className="
      fixed inset-0 z-[10000]
      bg-white
      flex flex-col
    "
    onClick={() => setViewerOpen(false)}
  >
    {/* TOP BAR */}
    <div className="absolute top-0 left-0 right-0 z-20 h-[64px] flex items-center justify-between px-4">
      <div className="text-[20px] text-[#444]">
        {activeIndex + 1} / {media.length}
      </div>

      <div className="flex items-center gap-0">
        {!isVideoMedia(activeMedia) && (
          <button
  type="button"
  onClick={(e) => {
  e.stopPropagation();

  setViewerZoomed((prev) => !prev);

  setTimeout(() => {
    const el = viewerScrollRef.current;
    if (!el) return;

    el.scrollTo({
      left: el.scrollWidth / 2 - el.clientWidth / 2,
      top: el.scrollHeight / 2 - el.clientHeight / 2,
      behavior: "smooth",
    });
  }, 80);
}}
  className="
    bg-white/85
    w-[38px] h-[38px]
    flex items-center justify-center
    text-[#444]
    active:scale-95
  "
  aria-label="Zoom"
>
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-[29px] h-[29px]"
    aria-hidden="true"
  >
    <circle
      cx="10.5"
      cy="10.5"
      r="5.8"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <path
      d="M15 15L20 20"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M10.5 7.8V13.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M7.8 10.5H13.2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
</button>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setViewerOpen(false);
          }}
          className="w-[38px] h-[38px] bg-white/85 text-[42px] leading-none text-[#444]"
          aria-label="Close"
        >
          ×
        </button>
      </div>
    </div>

    {/* MAIN VIEWER IMAGE */}
<div
  ref={viewerScrollRef}
  className={`
    flex-1
    relative
    flex items-center justify-center
    overflow-auto
    pt-[76px] pb-[150px]
    ${viewerZoomed ? "cursor-grab active:cursor-grabbing" : "cursor-default"}
  `}
  onClick={(e) => e.stopPropagation()}
>
      {isVideoMedia(activeMedia) ? (
        <video
          key={activeMediaUrl}
          src={activeMediaUrl}
          controls
          muted
          playsInline
          preload="metadata"
          className="max-w-full max-h-full object-contain"
        />
      ) : (
        <img
  src={activeMediaUrl}
  alt=""
  onClick={(e) => {
    e.stopPropagation();
    setViewerZoomed((prev) => !prev);

    setTimeout(() => {
      const el = viewerScrollRef.current;
      if (!el) return;

      el.scrollTo({
        left: el.scrollWidth / 2 - el.clientWidth / 2,
        top: el.scrollHeight / 2 - el.clientHeight / 2,
        behavior: "smooth",
      });
    }, 80);
  }}
  className={`
    transition-transform duration-200
    ${
      viewerZoomed
        ? "w-auto h-auto max-w-none max-h-none scale-[1.8] cursor-grab active:cursor-grabbing"
        : "max-w-full max-h-full object-contain cursor-zoom-in"
    }
  `}
/>
      )}

      {media.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              lang === "AR" ? nextMedia() : prevMedia();
            }}
            className="
  absolute left-4 top-1/2 -translate-y-1/2
  w-[58px] h-[58px]
  rounded-full
  bg-white
  text-[#555]
  text-[46px]
  font-light
  flex items-center justify-center
  shadow-[0_4px_18px_rgba(0,0,0,0.10)]
  active:scale-95
"
          >
            {lang === "AR" ? "›" : "‹"}
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              lang === "AR" ? prevMedia() : nextMedia();
            }}
            className="
  absolute right-4 top-1/2 -translate-y-1/2
  w-[58px] h-[58px]
  rounded-full
  bg-white
  text-[#555]
  text-[46px]
  font-light
  flex items-center justify-center
  shadow-[0_4px_18px_rgba(0,0,0,0.10)]
  active:scale-95
"
          >
            {lang === "AR" ? "‹" : "›"}
          </button>
        </>
      )}
    </div>

    {/* BOTTOM THUMBNAILS */}
    {media.length > 1 && (
      <div
  className="
    absolute left-0 right-0 bottom-0
    h-[145px]
    bg-[#8B8B8C] md:bg-transparent
    px-3 py-4
    overflow-x-auto
    no-scrollbar
    flex gap-3
    justify-center
    items-start
  "
  onClick={(e) => e.stopPropagation()}
>
        {media.map((m, i) => {
          const thumbUrl = getMediaUrl(m);
          const thumbIsVideo = isVideoMedia(m);
          const active = i === activeIndex;

          return (
            <button
              key={i}
              type="button"
              onClick={() => {
  setMediaDirection(i > activeIndex ? "next" : "prev");
  setActiveIndex(i);
  setViewerZoomed(false);
}}
              className={`
  shrink-0
  w-[72px] h-[72px]
  md:w-[72px] md:h-[105px]
  overflow-hidden
  bg-white
  border-2
  transition
  ${active ? "border-white md:border-black opacity-100" : "border-transparent opacity-65"}
`}
            >
              {thumbIsVideo ? (
  <video
    src={thumbUrl}
    muted
    playsInline
    preload="metadata"
    className="w-full h-full object-cover block"
  />
) : (
  <img
  src={thumbUrl}
  alt=""
  loading="lazy"
  decoding="async"
  className="w-full h-full object-cover"
/>
)}
            </button>
          );
        })}
      </div>
    )}
  </div>
)}

      <Footer className="mt-auto" />
    </div>
  );
}

export default ProductDetailsPage;
